import { h, icon, ICONS, type Child, type IconPath } from '../core/dom.ts';
import {
  actions,
  can,
  roleCan,
  selectedPhase,
  whyNot,
  type ModelImport,
  type ModelImportPhase,
  type State,
} from '../core/store.ts';
import * as api from '../core/api.ts';
import { PLACEHOLDER } from '../modeler/viewport.ts';
import { importBridgeModel } from '../modeler/importModel.ts';
import {
  button,
  emptyState,
  fact,
  foldHead,
  meter,
  refusalNote,
  revertOnRefusal,
  scrim,
  seg,
  sourced,
  stars,
  tag,
} from '../components/widgets.ts';
import { imageSlot } from '../components/imageSlot.ts';
import { panel, panelButton, panelRail } from '../components/panel.ts';
import { printDocument, savePdf, saveWord } from '../core/exportDoc.ts';
import { worksBlock } from './works.ts';
import { progressPhotoActions } from './progressPhotos.ts';
import { returnActions } from './returnsCalendar.ts';
import { worksActions } from './worksCalendar.ts';
import { plannedActivity, varianceSection } from './programme.ts';
import { discardGanttDraft, ganttDraftChanges, ganttToolsSlot } from './ganttBlock.ts';
import { baselineFoot, baselineHeadActions, baselinePage } from './baselinePage.ts';
import { conditionTrendBlock } from './conditionTrend.ts';
import { refusesRegister } from './suspended.ts';
import { mayRecordInService, stateEdits } from './stateDialog.ts';
import { appById, appForShell, type AppId } from '../core/apps.ts';
import { SCREEN_NAV } from '../components/navpane.ts';
import { ratingDueFor, ratingHints, ratingSlot } from '../rating/mount.ts';
import { reratingChip } from '../rating/screens/structures.ts';
import {
  cycleChipLabel,
  cycleLabel,
  findBridge,
  heroModel,
  meshAvailability,
  inspectionKindLabel,
  inspectionStanding,
  inspectionTypeLabel,
  type InspectionStanding,
  prettyDate,
  scaleMax,
  severityRank,
  geometryCaption,
} from '../data/derive.ts';
import { elementLabel, isRetiredElement } from '../data/elements.ts';
import {
  CONDITION_N,
  EXTENT_DEFINITIONS,
  URGENCY_DEFINITIONS,
} from '../data/ratingGuide.ts';
import { describeQuantity, fieldsFor, methodLabel, trimNumber } from '../data/quantities.ts';
import { CLASS_DIAGNOSTIC, isCostable, wordingMap } from '../data/remedial.ts';
import { estimateFor, npr, refusalText, totalCaveat } from '../data/estimate.ts';
import { consequenceFacts } from '../data/consequence.ts';
import { modelProvenance, provenanceOf } from '../data/provenance.ts';
import {
  bandLabel,
  bandRank,
  screenBridge,
  type ScreeningFacet,
} from '../data/screening.ts';
import {
  DEFECT_SEVERITIES,
  DEFECT_STATUSES,
  defectCodeLabel,
  defectMove,
  defectPhotoCount,
  isOpenStatus,
} from '../data/defects.ts';
import { bareLabel, indexComponentIds, indexComponentNames } from '../data/componentTree.ts';
import { count, join, metres, NOT_RECORDED, shown } from '../data/recorded.ts';
import { phaseChip, worksChip } from '../components/phaseChip.ts';
import { statusChip } from '../components/statusChip.ts';
import { decidedLine, operationalStatus, sinceLine } from '../data/posting.ts';
import {
  ACT_LABEL,
  WORKS_KIND_LABEL,
  accessLine,
  actOfRow,
  appAccess,
  phaseOf,
  stateKey,
} from '../data/phase.ts';
import { vocab } from '../tenant/agency.ts';
import { normaliseIdentity, rolesLabel } from '../data/roles.ts';
import { chainage } from '../tenant/format.ts';
import type {
  Bridge,
  BridgeView,
  ComponentNode,
  DetailView,
  InspectionState,
  ModelSummary,
  RegisterDefect,
} from '../data/types.ts';

/**
 * The widest scale drawn as stars. Above it a rating gets a proportion bar —
 * see ratingCell.
 */
const STAR_SCALE_LIMIT = 10;

/** Drag range, in px, for the details column — see beginResize. */
const MIN_PANEL_W = 260;
const MAX_PANEL_W = 720;
/**
 * Dragged this far past the minimum, the panel lets go and unpins, as a Visual
 * Studio tool window does when its splitter is pulled shut. Short of it the
 * width simply stops at the minimum, so a drag that overshoots a little is not
 * punished with a collapse.
 */
const SNAP_SHUT_PX = 120;
/** One arrow-key press on the splitter; Shift for a coarse step. */
const KEY_STEP = 16;

/**
 * What the record calls each stage of an import.
 *
 * Named stages rather than one "Working…" because they fail differently and are
 * worth telling apart while you wait: opening is the modeller's 1.6 MB chunk and
 * the model coming down, importing is the export being applied, and saving is
 * the result going to Cloudflare — which is the one that takes a few seconds
 * with nothing visibly happening.
 */
const IMPORT_PHASE: Record<ModelImportPhase, string> = {
  reading: 'Reading the export…',
  creating: 'Creating the model…',
  storing: 'Storing the model…',
  opening: 'Opening the model…',
  importing: 'Importing…',
  saving: 'Saving to the Cloud…',
  geometry: 'Storing the geometry…',
};

/**
 * The same phases, worded for a restore.
 *
 * A restore only ever reaches four of these — see beginModelRestore — but the
 * record is a full mapping so that a phase reaching it unexpectedly gets a label
 * rather than `undefined` on screen.
 */
const RESTORE_PHASE: Record<ModelImportPhase, string> = {
  reading: 'Fetching the model…',
  creating: 'Creating the model…',
  storing: 'Storing the model…',
  opening: 'Opening the model…',
  importing: 'Restoring on this device…',
  saving: 'Finishing…',
  geometry: 'Storing the geometry…',
};

/**
 * "Step 2 of 4 · Importing…" — the whole of what the record can honestly say.
 *
 * DISCRETE, NOT A PERCENTAGE. Nothing in the hidden frame reports how far through
 * applying an export it is, so a bar that filled at a guessed rate would be a
 * worse lie than no bar. What IS countable is the phases, and `steps` is the real
 * sequence this run will take — so the fraction is a fact rather than an estimate,
 * and it is the fraction that drives the width of the bar.
 */
function importProgress(run: ModelImport): { label: string; step: number; of: number } {
  const at = run.steps.indexOf(run.phase);
  return {
    // Same phases, different verb: see importCaption for why a restore must not
    // announce itself as an import.
    label: run.restoring ? RESTORE_PHASE[run.phase] : IMPORT_PHASE[run.phase],
    // A phase somehow not in the list counts as the first, rather than as zero of
    // n — an unknown position is not the same as no progress.
    step: at < 0 ? 1 : at + 1,
    of: run.steps.length,
  };
}

/**
 * The ring the action button spins while an import runs.
 *
 * A ring rather than a bar in this position: the button is a fixed height and the
 * label beside it already carries the phase, so what is wanted here is one glance
 * of "working", not a second copy of the reading. The bar is on the panel, where
 * there is room for it to mean something.
 */
function importRing(): HTMLElement {
  return h('span', { class: 'imp-ring', 'aria-hidden': 'true' });
}

/**
 * The record, in three panels: the explorer on the left, which is the record's
 * contents; the property panel beside it, which shows the one entry picked
 * there; and the 3D model, which has the rest of the screen to itself.
 *
 * The explorer is what the folded blocks used to be. The record had become a
 * column of headings under the model — inspections, screening, defects, the
 * remedial estimate, the change log, the photographs — each shut until it was
 * opened, so that the page read as a contents list. A contents list is what an
 * explorer is, and moving it there gives the model the full height of the
 * screen instead of a strip above a scroll of headings.
 *
 * Collapsing either side panel leaves its rail; collapsing both leaves the
 * model at full width.
 */
/**
 * The models saved against this record, or none where the index in hand is
 * another structure's (QA 2026-09-30). `findBridge` falls back to the first
 * record when the selection is not in the register yet — a bridge made moments
 * ago, before the re-read lands — and the record then paired that bridge's id
 * with the selected bridge's model in the still's URL, which 404s.
 */
function modelsOf(b: Pick<BridgeView, 'id'>, s: Readonly<State>): State['models']['models'] {
  return b.id === s.selectedId ? s.models.models : [];
}

export function detailScreen(s: Readonly<State>): HTMLElement {
  const b = findBridge(s.selectedId);
  /* Nothing to draw a record from. `findBridge` returns null only where the
     register itself is empty — a department configured before its bridge list
     arrived, or a device that has not hydrated — so this is the register saying
     it holds nothing, not this screen failing to find one structure. Both
     shells get it: a field inspector standing at a crossing needs the same
     answer as the desk. */
  if (b === null) return emptyState('This register has no structures in it yet.');
  if (s.shell === 'field') return fieldRecord(b, s);

  /* THE CANVAS, EDGE TO EDGE. The model's name sits in the panel's own header
     rather than over the view, and the geometry and levels it used to caption
     are in Bridge info. Import and Delete are on the structure's dashboard on
     the start page — see modelDeleteCommand. What is left in the body is the
     view and its toolbar (src/modeler/meshTools.ts). */
  /* The model's name is in the status bar, with the structure's id. */
  const modelActions = [modelHint(s, heroModel(modelsOf(b, s)), modelsOf(b, s).length)].filter((el) => el !== null);
  /* Which page docks a panel under the model, and that panel's name for the
     switch below. Null where the model has the content panel to itself. */
  const dockedName: string | null = defectsStacked(s)
    ? 'Inspection history'
    : historyPage(s)
      ? explorerEntry(s)?.view === 'condition'
        ? 'Condition'
        : 'Overall inspection history'
      : historyStacked(s)
        ? 'Overall inspection history'
        : null;
  const model = (): HTMLElement => panel(
    {
      title: '3D model',
      class: 'det-main',
      actions: dockedName ? [...modelActions, stackSwitch(s, dockedName)] : modelActions,
    },
    /* No strip of links to the other applications: the ribbon's tabs are
       those applications, and they open on this structure. */
    hero(b, s),
    /* WHAT IS NOT HERE, AND USED TO BE: the site record and the programme.
       Both were on this page while there was one application — the Works
       card under the defects and the Programme card under that — and both
       belong to other applications now: Construction Monitoring owns what
       was built and when, Construction Scheduler owns the plan and the
       variance against it. See src/core/apps.ts.

       NEITHER IS REIMPLEMENTED HERE IN ANY FORM. Not a count, not a "3
       entries recorded" line: both would need the six per-structure reads
       this screen no longer makes (see `loadBridgeContext`), and a number
       drawn from a read nobody makes is a number that is quietly stale. */
  );
  /* THE DEFECTS PAGE STACKS UNDER THE MODEL rather than beside it: the model
     over a panel of two tabs, the defects and their photographs, with no
     property panel between the explorer and them. A defect is found on the
     model and read underneath it. */
  /* Bridge Rating's ratings on record are read in the content panel, with no
     property panel beside them. */
  if (ratingsOnRecord(s)) {
    return recordShell(
      b,
      s,
      panel(
        { title: 'Ratings on record', class: ['det-main', 'det-main--docked', 'det-main--rating-history'] },
        ratingSlot('rating-history'),
      ),
      true,
    );
  }
  /* The stack keeps both panels in the tree whichever the switch shows, and
     the CSS folds the other away: the model's header stays as a bar holding
     the switch, and the mesh view, finding its slot with no height, hides
     itself rather than being torn down and rebuilt. */
  const stack = (under: HTMLElement, over?: HTMLElement | null): HTMLElement =>
    h('div', { class: ['det-stack', s.detailStackView !== 'both' && `det-stack--${s.detailStackView}`] }, model(), under, over ?? null);
  return defectsStacked(s)
    ? recordShell(b, s, stack(defectsTabs(b, s), defectFocusDialog(b, s)), true)
    : historyPage(s)
      ? recordShell(
          b,
          s,
          stack(explorerEntry(s)?.view === 'condition' ? conditionPanel(b) : inspectionsPanel(b, s)),
          true,
        )
      : historyStacked(s)
      ? recordShell(b, s, stack(inspectionsPanel(b, s)))
      : recordShell(b, s, model());
}

/**
 * The switch in the model's header on a stacked page: both panels, the model
 * alone, or the docked panel alone. One choice for every stacked page, and
 * remembered, as the explorer's collapse is.
 */
function stackSwitch(s: Readonly<State>, docked: string): HTMLElement {
  const view = s.detailStackView;
  const el = seg([
    {
      label: 'Both',
      on: view === 'both',
      title: `The 3D model over the ${docked.toLowerCase()}`,
      onClick: () => actions.setDetailStackView('both'),
    },
    {
      label: '3D view',
      on: view === 'model',
      title: 'The 3D model on its own, full height',
      onClick: () => actions.setDetailStackView('model'),
    },
    {
      label: docked,
      on: view === 'records',
      title: `The ${docked.toLowerCase()} on its own, full height`,
      onClick: () => actions.setDetailStackView('records'),
    },
  ]);
  el.classList.add('det-stack-switch');
  el.setAttribute('aria-label', 'Show');
  return el;
}

/**
 * Whether Bridge Inspection's record docks the inspection history under the
 * model: on every page of its explorer but Defects, which docks the defects,
 * and Inspection history, which is the history itself.
 */
function historyStacked(s: Readonly<State>): boolean {
  const view = explorerEntry(s)?.view;
  return (
    s.shell !== 'field' &&
    appForShell(s.shell).id === 'inspection' &&
    view !== undefined &&
    view !== 'defects' &&
    view !== 'inspections'
  );
}

/**
 * Whether the record draws its Inspection history or Condition page: that page
 * under the model, as Defects is, with no property panel beside them.
 */
function historyPage(s: Readonly<State>): boolean {
  const view = explorerEntry(s)?.view;
  return s.shell !== 'field' && (view === 'inspections' || view === 'condition');
}

/** The Condition page's panel under the model: the condition trend. */
function conditionPanel(b: BridgeView): HTMLElement {
  return panel(
    { title: 'Condition', class: ['det-inv', 'det-tabs'] },
    h(
      'div',
      { class: 'det-trend' },
      conditionTrendBlock(b.inspections) ?? emptyState('No condition on record to draw a trend from.'),
    ),
  );
}

/** Whether the record draws Bridge Rating's ratings on record in the content panel. */
function ratingsOnRecord(s: Readonly<State>): boolean {
  return s.shell !== 'field' && s.screen === 'detail' && explorerEntry(s)?.view === 'ratingHistory';
}

/** Whether the record draws its Defects page under the model — see detailScreen. */
function defectsStacked(s: Readonly<State>): boolean {
  return s.shell !== 'field' && explorerEntry(s)?.view === 'defects';
}

/** The panel under the model on the Defects page: the defects, or their photographs. */
function defectsTabs(b: BridgeView, s: Readonly<State>): HTMLElement {
  const photos = s.defectsTab === 'photos';
  return panel(
    {
      title: 'Inspection history',
      class: ['det-inv', 'det-tabs'],
      tabs: [
        {
          label: 'Inspection',
          count: explorerHint(b, s, 'defects'),
          on: !photos,
          onclick: () => actions.showDefectsTab('defects'),
        },
        {
          label: 'Photos',
          count: explorerHint(b, s, 'photos'),
          on: photos,
          onclick: () => actions.showDefectsTab('photos'),
        },
      ],
    },
    photos ? photosBlock(b, s) : defectsBlock(b, s),
  );
}

/**
 * The defect picked, in full, as a dialog — or null. Beside the stack's panels
 * rather than in the docked one, which the 3D view alone folds away.
 */
function defectFocusDialog(b: BridgeView, s: Readonly<State>): HTMLElement | null {
  const focus = s.defectFocusId === null
    ? undefined
    : s.registerDefects.find((d) => d.id === s.defectFocusId && d.bridgeId === b.id);
  return focus ? defectDetail(b, focus, s) : null;
}

/* --------------------------- screens in the content panel --------------------------- */

/**
 * The applications' screens, drawn in the record's content panel — where the 3D
 * model is — rather than in place of the whole record, so the explorer and the
 * property panel stay put beside them in every application. Named as the ribbon
 * names them, since that is where they were opened from.
 *
 * The 3D model itself is not here: it is `detail`, the screen this layout is
 * built round, and every application now owns it (src/core/apps.ts), so the
 * model is one ribbon command away wherever you are.
 */
const DOCKED: Partial<Record<State['screen'], string>> = {
  network: 'Network',
  report: 'Inventory sheet',
  sheet: 'Inspection summary sheet',
  summary: 'Network summary',
  schedule: 'Programme',
  site: 'Site record',
  works: 'Period report',
  guide: 'Guide',
};

/**
 * Whether this screen is drawn inside the record's content panel.
 *
 * Every application on the desk: the field shell has no side panels to keep,
 * and the start page is about the register as a whole rather than one
 * structure. Bridge Rating's own screens, which this store cannot name, dock
 * whole — whatever its store is showing goes in the content panel. And only
 * with a structure to show, since the side panels are that structure's.
 */
export function docksInRecord(s: Readonly<State>, app: AppId): boolean {
  if (s.shell === 'field' || app === 'home' || s.screen === 'detail') return false;
  if (findBridge(s.selectedId) === null) return false;
  /* NOT THE REGISTER, which DOCKED leaves out. It is how a structure is picked —
     the title bar's search lands on it — and drawn beside panels that already
     show one it would be a list of every structure next to the one in hand. */
  return appById(app).separate === true || DOCKED[s.screen] !== undefined;
}

/**
 * The record's explorer and property panel, with `body` in the content panel.
 * `title` names what is docked, where the screen alone does not say — Bridge
 * Rating's, which this store does not know by name.
 */
export function recordWith(
  s: Readonly<State>,
  body: HTMLElement,
  docked: { title: string; key: string; document?: boolean } = { title: dockedTitle(s), key: s.screen },
): HTMLElement {
  const b = findBridge(s.selectedId);
  if (b === null) return body;
  /* A screen of an application this structure's state hides (LC-13) is the
     one line saying why, and when it opens — not the screen, and not a blank. */
  const app = appForShell(s.shell).id;
  const hiddenLine =
    app !== 'home' && appHidden(s, app) && (appById(app).separate === true || HIDDEN_WITH_APP.has(s.screen))
      ? accessLine(phaseOf(b), app)
      : null;
  const periodBar = docked.key === 'works' ? liftPeriodBar(body) : null;
  return recordShell(
    b,
    s,
    panel(
      {
        title: docked.title,
        class: ['det-main', 'det-main--docked', `det-main--${docked.key}`],
        lead: [periodBar],
        actions: docked.document === true || DOCUMENTS.has(docked.key)
          ? documentActions(documentName(docked, b))
          : docked.key === 'site' && siteView(s) === 'progressPhotos'
            ? progressPhotoActions(s)
            : docked.key === 'site' && siteView(s) === 'returns'
              ? returnActions(s)
              : docked.key === 'site' && siteView(s) === 'works'
                ? worksActions(s)
              : docked.key === 'schedule'
                ? [
                    ...(s.scheduleView === 'gantt' && s.scheduleLayout === 'gantt' ? draftActions() : []),
                    s.scheduleView === 'gantt' && s.scheduleLayout === 'gantt' ? ganttToolsSlot() : null,
                    scheduleLayoutSwitch(s),
                  ]
                : [],
      },
      hiddenLine === null ? body : emptyState(hiddenLine),
    ),
    /* The programme's Gantt and planned activity have the width to themselves
       until a page of the explorer is picked, which opens beside them. */
    s.screen === 'schedule' && !s.schedulePanel,
  );
}

/** The Gantt's edits: thrown away, or proposed as a revision of the baseline. */
function draftActions(): HTMLElement[] {
  const n = ganttDraftChanges();
  if (n === 0) return [];
  return [
    panelButton({
      icon: ICONS.refresh,
      text: 'Discard changes',
      title: 'Put the Gantt back to the baseline in force',
      onclick: () => discardGanttDraft(),
    }),
    panelButton({
      icon: ICONS.edit,
      text: 'Propose revision',
      title: `${n} ${n === 1 ? 'change' : 'changes'} to the baseline, taken as a revision for an approver to accept`,
      onclick: () => actions.openBaselineForm(),
    }),
  ];
}

/**
 * The Programme's three faces: the baseline's Gantt, the table of planned
 * activity, or a month calendar of whichever of the two was last in front.
 */
function scheduleLayoutSwitch(s: Readonly<State>): HTMLElement {
  const bars = s.scheduleLayout === 'gantt';
  const show = (view: State['scheduleView']) => () => {
    actions.showSchedule(view);
    actions.setScheduleLayout('gantt');
  };
  const el = seg([
    {
      label: 'Gantt',
      leadIcon: ICONS.gantt,
      on: bars && s.scheduleView === 'gantt',
      title: 'Show the frozen baseline as a Gantt chart',
      onClick: show('gantt'),
    },
    {
      label: 'Table',
      leadIcon: ICONS.list,
      on: bars && s.scheduleView === 'activities',
      title: 'Show every planned activity against what the site has recorded',
      onClick: show('activities'),
    },
    {
      label: 'Calendar',
      leadIcon: ICONS.calendar,
      on: !bars,
      title: 'Show the programme on a month calendar',
      onClick: () => actions.setScheduleLayout('calendar'),
    },
  ]);
  el.classList.add('seg--head');
  el.setAttribute('aria-label', 'Show as');
  return el;
}

/**
 * The docked screens that are one application's record of this structure, and
 * go when the application does. The network screens are about the register,
 * not the structure in hand, and stay.
 */
const HIDDEN_WITH_APP: ReadonlySet<State['screen']> = new Set<State['screen']>(['sheet']);

/** Whether the selected structure's state hides this application (LIFECYCLE.md §3). */
function appHidden(s: Readonly<State>, app: AppId): boolean {
  if (app === 'home') return false;
  const p = selectedPhase(s);
  return p !== null && appAccess(p, app).mode === 'hidden';
}

/**
 * The line under the explorer: why an application is hidden or read-only for
 * this structure, or the scope §3 gives it — design check only while it is
 * being built, special inspections only once it is closed. Null for an
 * application simply open.
 */
function accessNote(s: Readonly<State>, app: AppId): string | null {
  if (app === 'home') return null;
  const p = selectedPhase(s);
  if (p === null) return null;
  const line = accessLine(p, app);
  if (line !== null) return line;
  const key = stateKey(p);
  const scope = appAccess(p, app).scope;
  if (!scope || (key !== 'construction' && key !== 'closed')) return null;
  return `${scope} while this structure is ${key === 'closed' ? 'closed' : 'under construction'}.`;
}

/** The docked screens that are documents: printed, and saved as PDF or Word. */
const DOCUMENTS: ReadonlySet<string> = new Set(['report', 'sheet', 'summary', 'works']);

/** What a saved copy is called: the document, its structure where it has one, and the day. */
function documentName(docked: { title: string; key: string }, b: BridgeView): string {
  const day = todayIso();
  return docked.key === 'report' || docked.key === 'sheet' ? `${docked.title} ${b.id} ${b.name} ${day}` : `${docked.title} ${day}`;
}

/** Print, PDF and Word, for the head of a docked document's panel. */
function documentActions(name: string): HTMLElement[] {
  /* The document is the panel's body, found from the button so the export takes
     exactly what is on screen — or, where the body is a slot another store
     paints (Bridge Rating's Method), what is painted in it: the export drops
     `data-keep` boxes. */
  const bodyOf = (e: Event): HTMLElement | null => {
    const body = (e.currentTarget as HTMLElement | null)
      ?.closest('.panel')
      ?.querySelector<HTMLElement>(':scope > .panel__body');
    return body?.querySelector<HTMLElement>(':scope > [data-keep] > *') ?? body ?? null;
  };
  return [
    panelButton({ icon: ICONS.print, text: 'Print', title: 'Print this document', onclick: () => printDocument() }),
    panelButton({
      icon: ICONS.report,
      text: 'PDF',
      title: 'Save this document as a PDF — choose “Save as PDF” in the print dialog',
      onclick: () => savePdf(name),
    }),
    panelButton({
      icon: ICONS.import,
      text: 'Word',
      title: 'Save this document as a Word document (.docx)',
      onclick: (e) => {
        const body = e ? bodyOf(e) : null;
        if (body) void saveWord(body, name);
      },
    }),
  ];
}

/** The docked screen's name: for Monitoring's site record, the one list the explorer picked. */
function dockedTitle(s: Readonly<State>): string {
  const site = siteView(s);
  if (s.screen === 'site' && site !== null) {
    return MONITORING_EXPLORER.find((e) => e.view === site)?.label ?? '';
  }
  return DOCKED[s.screen] ?? '';
}

/**
 * Whether the record's explorer and property panel are on screen: the desk
 * record itself, or an application's screen docked beside them.
 */
export function recordShown(s: Readonly<State>): boolean {
  if (s.shell === 'field' || findBridge(s.selectedId) === null) return false;
  if (refusesRegister(s, s.screen)) return false;
  return s.screen === 'detail' || docksInRecord(s, appForShell(s.shell).id);
}

/**
 * The explorer, the property panel and the splitter, then `main`. `stacked`
 * leaves out the property panel: `main` carries its own page under the model.
 */
function recordShell(b: BridgeView, s: Readonly<State>, main: HTMLElement, stacked = false): HTMLElement {
  /* DOCKED TOOL WINDOWS, in the manner of Visual Studio: a thin title bar
     with the window's name and its pushpin, a splitter to the right of the
     property panel that drags, and — unpinned — a rail with the name written
     down it, which is the whole of the way back. The rail is always there when
     the panel is not, so a collapse remembered from last time can always be
     undone. */
  /* Beside the programme the page is open or gone — its pin closes it — never a rail. */
  const collapsed = s.detailPanelCollapsed && s.screen !== 'schedule';
  const explorerCollapsed = s.detailExplorerCollapsed;
  const entry = explorerEntry(s);
  const planned = plannedShown(s);
  const plannedRail = planned || (monitoring(s) && entry !== null && SITE_VIEWS.has(entry.view));

  return h(
    'div',
    {
      class: ['det-cols', explorerCollapsed && 'det-cols--exp-collapsed', stacked && 'det-cols--stack'],
      style: { '--det-w': collapsed ? '28px' : `${s.detailPanelWidth}px` },
    },

    explorerCollapsed
      ? panelRail({
          title: 'Explorer',
          icon: ICONS.list,
          onclick: () => actions.toggleDetailExplorer(),
        })
      : panel(
          {
            title: 'Explorer',
            /* The structure's phase chip is at the end of the ribbon (LC-10). */
            actions: [
              panelButton({
                icon: ICONS.pin,
                title: 'Auto Hide — collapse to the side',
                label: 'Collapse the explorer panel',
                onclick: () => actions.toggleDetailExplorer(),
              }),
            ],
            class: 'det-exp',
          },
          explorer(b, s),
        ),

    stacked
      ? null
      : collapsed
      ? panelRail({
          title: plannedRail ? PLANNED_TITLE : (entry?.label ?? appForShell(s.shell).label),
          icon: plannedRail ? ICONS.gantt : ICONS.book,
          onclick: () => actions.toggleDetailPanel(),
        })
      : planned
        ? panel({ title: PLANNED_TITLE, class: 'det-inv' }, plannedActivity(s))
        : entry === null
          ? panel(
              { title: appForShell(s.shell).label, class: 'det-inv' },
              emptyState(accessNote(s, appForShell(s.shell).id) ?? ''),
            )
          : propertyPanel(b, s, entry),

    stacked ? null : collapsed ? h('div', { class: 'det-gap' }) : splitter(s.detailPanelWidth),

    main,
  );
}

/* --------------------------- the explorer --------------------------- */

interface ExplorerEntry {
  view: DetailView;
  label: string;
  icon: IconPath;
  /** Places on the entry's page, listed under it; each scrolls to its `data-group`. */
  children?: readonly { group: string; label: string }[];
  /** The caption of the group the entry is listed under, where its explorer groups them. */
  section?: string;
}

/**
 * The groups of the inventory, as the explorer lists them under Bridge info.
 * The names are `section`'s own, so an entry here finds its group by them.
 */
const INVENTORY_GROUPS: readonly { group: string; label: string }[] = [
  { group: 'details', label: 'Location' },
  { group: 'structure', label: 'Structure & levels' },
  { group: 'consequence', label: 'Consequence of closure' },
  { group: 'construction', label: 'Construction details' },
  { group: 'history', label: 'History' },
];

/**
 * What the structure is and where it is in its life: Bridge's explorer, and
 * no other application's. The reference tables follow it — see `referenceTables`.
 */
const BRIDGE_EXPLORER: readonly ExplorerEntry[] = [
  { view: 'inventory', label: 'Bridge info', icon: ICONS.book, children: INVENTORY_GROUPS },
  { view: 'state', label: 'State', icon: ICONS.refresh },
];

/**
 * Bridge Inspection's entries, in the order it lists them — the order the blocks
 * stacked in under the model. Remedial work and the photographs are on the
 * Defects page, under the defects they are of.
 */
const EXPLORER: readonly ExplorerEntry[] = [
  { view: 'inspections', label: 'Overall inspection history', icon: ICONS.clipboard },
  { view: 'condition', label: 'Condition', icon: ICONS.chart },
  { view: 'defects', label: 'Inspection history', icon: ICONS.alert },
];


/**
 * EACH APPLICATION'S EXPLORER: that application's own record of the structure,
 * and nothing of another application's. What the structure is — Bridge info
 * and State — is Bridge's. Bridge Inspection's is `EXPLORER` above.
 *
 * Bridge Rating: the rating — its form in the property panel, one entry per
 * part of it, with the answer in the content panel — then the ratings of this
 * structure already on record.
 */
const RATING_EXPLORER: readonly ExplorerEntry[] = [
  {
    view: 'rating',
    label: 'Load rating',
    icon: ICONS.scale,
    children: [
      { group: 'what', label: 'What is being rated' },
      { group: 'layout', label: 'Layout and section' },
      { group: 'materials', label: 'Materials' },
      { group: 'note', label: 'Note' },
    ],
  },
  { view: 'ratingHistory', label: 'Ratings on record', icon: ICONS.report },
];

/** Construction Scheduler: the frozen baseline, and the variance against it. */
const SCHEDULER_EXPLORER: readonly ExplorerEntry[] = [
  { view: 'baseline', label: 'Baseline', icon: ICONS.gantt },
  { view: 'variance', label: 'Variance', icon: ICONS.calendar },
];

/**
 * Construction Monitoring: the site record, one page per kind of record, under
 * a caption per group. The planned activity heads it and the period report
 * closes Reports — see `monitoringExplorer`.
 */
const MONITORING_EXPLORER: readonly ExplorerEntry[] = [
  { view: 'works', label: 'Works', icon: ICONS.hardhat, section: 'Site diary' },
  { view: 'incidents', label: 'Incidents', icon: ICONS.incident, section: 'Site diary' },
  { view: 'progressPhotos', label: 'Progress photos', icon: ICONS.image, section: 'Site diary' },
  { view: 'documents', label: 'Documents', icon: ICONS.folder, section: 'Records' },
  { view: 'charts', label: 'Charts', icon: ICONS.chart, section: 'Reports' },
];

/** An application's explorer, whatever the structure's state. */
function explorerOf(app: AppId): readonly ExplorerEntry[] {
  return app === 'structure'
    ? BRIDGE_EXPLORER
    : app === 'rating'
      ? RATING_EXPLORER
      : app === 'scheduler'
        ? SCHEDULER_EXPLORER
        : app === 'monitoring'
          ? MONITORING_EXPLORER
          : EXPLORER;
}

/** The explorer of the application on screen. */
function explorerEntries(s: Readonly<State>): readonly ExplorerEntry[] {
  const app = appForShell(s.shell).id;
  /* An application this structure's state hides (LC-13) lists nothing of its
     own record, only why — see `accessNote`. */
  return appHidden(s, app) ? [] : explorerOf(app);
}

/** Monitoring's site lists, which open in the content panel rather than the property panel. */
const SITE_VIEWS: ReadonlySet<DetailView> = new Set<DetailView>([
  'works',
  'returns',
  'incidents',
  'documents',
  'tests',
  'siteItems',
  'charts',
  'progressPhotos',
]);

function monitoring(s: Readonly<State>): boolean {
  return appForShell(s.shell).id === 'monitoring';
}

/** The site list Monitoring's explorer last picked, or null. */
export function siteView(s: Readonly<State>): DetailView | null {
  return monitoring(s) && SITE_VIEWS.has(s.detailView) ? s.detailView : null;
}

const PLANNED_TITLE = 'Planned activity';

/**
 * Whether the property panel is showing the programme's planned activity:
 * in Monitoring, beside the site record or the period report in the content panel.
 */
function plannedShown(s: Readonly<State>): boolean {
  return monitoring(s) && (s.screen === 'site' || s.screen === 'works');
}

/**
 * The entry the property panel is showing — the first, where the app has no such
 * entry — or null where the explorer lists none, the app being hidden here.
 */
function explorerEntry(s: Readonly<State>): ExplorerEntry | null {
  const entries = explorerEntries(s);
  const found = entries.find((e) => e.view === s.detailView);
  /* Ratings on record are a page of the content panel, drawn over the 3D
     model's place (see ratingsOnRecord): beside another of Bridge Rating's
     screens, the property panel keeps the rating form. */
  const contentOnly = found !== undefined && (SITE_VIEWS.has(found.view) || (found.view === 'ratingHistory' && s.screen !== 'detail'));
  return found === undefined || contentOnly ? (entries[0] ?? null) : found;
}

/** Whether an explorer entry is lit: in Monitoring, by what the content panel is showing. */
function entrySelected(s: Readonly<State>, view: DetailView): boolean {
  /* Beside the programme a page is lit only while it is open. */
  if (s.screen === 'schedule') return s.schedulePanel && view === explorerEntry(s)?.view;
  if (!monitoring(s)) return view === explorerEntry(s)?.view;
  if (SITE_VIEWS.has(view)) return s.screen === 'site' && s.detailView === view;
  return !plannedShown(s);
}

/**
 * Pick an explorer entry. In Monitoring an entry sets both panels: a site list
 * in the content panel beside the planned activity.
 */
function pickEntry(s: Readonly<State>, view: DetailView, group?: string): void {
  if (monitoring(s)) {
    const screen = SITE_VIEWS.has(view) ? 'site' : 'detail';
    if (s.screen !== screen) actions.go(screen);
  }
  if (view === 'ratingHistory' && s.screen !== 'detail') actions.go('detail');
  showEntry(view, group);
}

/**
 * What the explorer prints beside an entry: the same count the block's own
 * heading carries, so the explorer still says which entries are worth opening.
 * Cheap reads only — the remedial estimate is not priced here to label a row.
 */
function explorerHint(b: BridgeView, s: Readonly<State>, view: DetailView): string {
  const mine = s.registerDefects.filter((d) => d.bridgeId === b.id);
  switch (view) {
    case 'inventory':
    case 'state':
      return '';
    case 'inspections':
      return b.inspections.length ? String(b.inspections.length) : '';
    case 'condition':
      return '';
    case 'defects': {
      const open = mine.filter((d) => isOpenStatus(d.status)).length;
      return mine.length ? `${open} open` : '';
    }
    case 'changes':
      return s.changesOpen && s.changes ? String(s.changes.length) : '';
    case 'rating': {
      const n = ratingHints(b.id).outstanding;
      return n === null ? '' : n === 0 ? 'Ready' : `${n} to go`;
    }
    case 'ratingHistory': {
      const n = ratingHints(b.id).onRecord;
      return n ? String(n) : '';
    }
    case 'baseline':
      return s.baseline === null ? 'None' : String(s.baseline.activities.length);
    case 'variance':
      return '';
    case 'works':
      return s.progressRead === 'read' && s.progressSummary.count ? String(s.progressSummary.count) : '';
    case 'returns':
      return s.progressRead === 'read' && s.siteReturns.length ? String(s.siteReturns.length) : '';
    case 'incidents':
      return s.progressRead === 'read' && s.incidents.length ? String(s.incidents.length) : '';
    case 'documents':
      return s.progressRead === 'read' && s.documents.length ? String(s.documents.length) : '';
    case 'tests': {
      const failed = s.tests.filter((t) => !t.passed).length;
      return s.progressRead !== 'read' || !s.tests.length ? '' : failed ? `${failed} failed` : String(s.tests.length);
    }
    case 'siteItems': {
      const open = s.items.filter((i) => i.status !== 'closed').length;
      return s.progressRead === 'read' && open ? `${open} open` : '';
    }
    case 'charts':
      return '';
    case 'progressPhotos': {
      const n = s.progress.filter((e) => e.hasPhoto && !e.pending).length;
      return s.progressRead === 'read' && n ? String(n) : '';
    }
    case 'photos': {
      const n = mine.reduce((sum, d) => sum + defectPhotoCount(d), 0);
      return n ? String(n) : '';
    }
  }
}

/** The explorer: the record's contents, one entry per page of the property panel. */
function explorer(b: BridgeView, s: Readonly<State>): HTMLElement {
  const entries = explorerEntries(s);
  return h(
    'nav',
    { class: 'exp', 'aria-label': `Contents of the record for ${b.id}` },
    ...(monitoring(s) && entries.length
      ? monitoringExplorer(b, s, entries)
      : entries.map((e) => explorerGroup(b, s, e))),
    /* Why this application is hidden or read-only here, in one line (LC-13). */
    ((note) => (note === null ? null : h('div', { class: 'exp__note', text: note })))(
      accessNote(s, appForShell(s.shell).id),
    ),
    referenceTables(b, s),
  );
}

/**
 * Monitoring's explorer, under a caption per group: the programme's planned
 * activity — what the property panel shows — then the site record's pages,
 * with the period report, a screen of its own, closing Reports.
 */
function monitoringExplorer(b: BridgeView, s: Readonly<State>, entries: readonly ExplorerEntry[]): HTMLElement[] {
  const section = (cap: string, ...rows: (HTMLElement | null)[]) =>
    h('div', { class: 'exp__group exp__group--sec', role: 'group', 'aria-label': cap }, h('div', { class: 'exp__cap', text: cap }), ...rows);
  const caps = [...new Set(entries.map((e) => e.section ?? ''))];
  return [
    section(
      'Programme',
      explorerRow({
        icon: ICONS.gantt,
        label: 'Planned',
        title: PLANNED_TITLE,
        onclick: () => showPlanned(s),
      }),
    ),
    ...caps.map((cap) =>
      section(
        cap,
        ...entries.filter((e) => (e.section ?? '') === cap).map((e) => explorerGroup(b, s, e)),
        cap === 'Reports'
          ? explorerRow({
              icon: SCREEN_NAV.works.iconPath,
              label: SCREEN_NAV.works.label,
              selected: s.screen === 'works',
              onclick: () => actions.go('works'),
            })
          : null,
      ),
    ),
  ];
}

/** Bring the planned activity into view: the property panel opened, at its top. */
function showPlanned(s: Readonly<State>): void {
  if (s.detailPanelCollapsed) actions.toggleDetailPanel();
  requestAnimationFrame(() => {
    const body = document.querySelector<HTMLElement>('.det-inv .panel__body');
    if (body) body.scrollTop = 0;
  });
}

/** An explorer entry, with the places on its page listed under it. */
function explorerGroup(b: BridgeView, s: Readonly<State>, e: ExplorerEntry): HTMLElement {
  return h(
    'div',
    { class: 'exp__group' },
    explorerRow({
      icon: e.icon,
      label: e.label,
      hint: explorerHint(b, s, e.view),
      selected: entrySelected(s, e.view),
      onclick: () => pickEntry(s, e.view),
    }),
    e.children
      ? h(
          'div',
          { class: 'exp__children', role: 'group', 'aria-label': e.label },
          ...e.children.map((g) =>
            explorerRow({
              label: g.label,
              child: true,
              onclick: () => pickEntry(s, e.view, g.group),
            }),
          ),
        )
      : null,
  );
}

/**
 * Whether the signed-in account keeps the reference tables, at a desk. Asked of
 * a known identity only: `roleCan` answers yes while the register has not said who is
 * signed in, and these tables are the administrator's alone.
 */
function keepsReference(s: Readonly<State>): boolean {
  /* Register administration, and absent from the field app for the reason the
     bulk import is: nobody keeps a rate table on a phone on site. */
  return s.shell !== 'field' && s.identity !== null && roleCan(s, 'reference.manage');
}

/**
 * The supporting tables Bridge Inspection's estimate is built from, under a rule
 * at the foot of Bridge's explorer — the administrator's only.
 */
function referenceTables(b: BridgeView, s: Readonly<State>): HTMLElement | null {
  if (appForShell(s.shell).id !== 'structure' || !keepsReference(s)) return null;
  return h(
    'div',
    { class: 'exp__group exp__group--ref', role: 'group', 'aria-label': 'Reference tables' },
    h('div', { class: 'exp__cap', text: 'Reference tables' }),
    explorerRow({
      icon: ICONS.scale,
      label: 'District rates',
      hint: s.rateTables.length ? String(s.rateTables.length) : '',
      selected: s.ratesOpen,
      /* Seeded with this structure's district: the commonest reason to open it
         is that the remedial work says there are no rates for exactly that one. */
      onclick: () => actions.openRates(b.district ?? ''),
    }),
    explorerRow({
      icon: ICONS.hardhat,
      label: 'Recommended work',
      hint: s.treatmentWording.length ? `${s.treatmentWording.length} reworded` : '',
      selected: s.treatmentsOpen,
      onclick: () => actions.openTreatments(),
    }),
  );
}

/** The views each application's explorer lists, Bridge info aside — held to `EXPLORER_PAGES` by a check. */
export function explorerViews(app: AppId): DetailView[] {
  return explorerOf(app).map((e) => e.view).filter((v) => v !== 'inventory');
}

function explorerRow(opts: {
  label: string;
  icon?: IconPath;
  hint?: string;
  selected?: boolean;
  child?: boolean;
  /** The tooltip, where it says more than the label. */
  title?: string;
  onclick: () => void;
}): HTMLElement {
  return h(
    'button',
    {
      type: 'button',
      class: ['exp__item', opts.child && 'exp__item--child', opts.selected && 'is-selected'],
      'aria-current': opts.selected ? 'true' : undefined,
      title: opts.title ?? opts.label,
      onclick: opts.onclick,
    },
    opts.icon ? icon(opts.icon, { size: 14, width: 1.7, class: 'exp__icon' }) : null,
    h('span', { class: 'exp__label', text: opts.label }),
    opts.hint ? h('span', { class: 'exp__hint', text: opts.hint }) : null,
  );
}

/**
 * Show an entry in the property panel, and put the panel's scroll where the
 * reader asked for: the top of a new page, or the inventory group they picked.
 * After the repaint, since the group is not in the DOM until then.
 */
function showEntry(view: DetailView, group?: string): void {
  actions.showDetailView(view, group);
  requestAnimationFrame(() => {
    const body = document.querySelector<HTMLElement>('.det-inv .panel__body');
    if (!body) return;
    const target = group ? body.querySelector<HTMLElement>(`[data-group="${group}"]`) : null;
    if (target) target.scrollIntoView({ block: 'start', behavior: 'smooth' });
    else body.scrollTop = 0;
  });
}

/* --------------------------- the property panel --------------------------- */

/**
 * The property panel's page for the entry picked in the explorer. A block is
 * drawn held open — see `docked` — and a block with nothing to draw says so,
 * rather than leaving a panel with a title over nothing.
 */
function propertyBody(b: BridgeView, s: Readonly<State>, view: DetailView): HTMLElement[] {
  switch (view) {
    case 'inventory':
      return [
        inventorySummary(asModelled(b, s)),
        detailsBlock(b, s),
        structureBlock(b, s),
        consequenceCard(b, s),
        constructionCard(b, s),
        historyCard(b, s),
      ];
    case 'inspections':
      return [conditionBlock(b, s)];
    case 'condition':
      return [conditionTrendBlock(b.inspections) ?? emptyState('No condition on record to draw a trend from.')];
    case 'state':
      return [stateBlock(b, s)];
    case 'defects':
      return [defectsBlock(b, s), defectFocusDialog(b, s), photosBlock(b, s)].filter((el) => el !== null);
    case 'changes':
      return [historyBlock(b, s)];
    case 'photos':
      return [photosBlock(b, s)];
    /* Bridge Rating's pages, painted from its own store into boxes this render
       leaves alone — see src/rating/mount.ts. */
    case 'rating':
      return [ratingSlot('rating-form')];
    case 'ratingHistory':
      return [ratingSlot('rating-history')];
    case 'baseline':
      return [baselinePage(b.name, s)];
    case 'variance':
      return [varianceSection(s, b.name, true)];
    /* Monitoring's site lists are drawn in the content panel (src/screens/site.ts),
       with the planned activity here beside them. */
    case 'works':
    case 'returns':
    case 'incidents':
    case 'documents':
    case 'tests':
    case 'siteItems':
    case 'charts':
    case 'progressPhotos':
      return [plannedActivity(s)];
  }
}

/**
 * Whether a block is the one the property panel is showing, which holds it open
 * with no fold on its heading. Desk only: the field record draws some of the
 * same blocks and folds them as it always has.
 */
function docked(s: Readonly<State>, id: string): boolean {
  /* Remedial work and the photographs are drawn on the Defects page, under the defects they are of. */
  return (
    s.shell !== 'field' &&
    (s.detailView === id ||
      ((id === 'remedial' || id === 'photos') && s.detailView === 'defects') ||
      (id === 'inspections' && (historyStacked(s) || historyPage(s))))
  );
}

/** The fold on a block's heading — none on the block the panel is showing. */
function foldToggle(s: Readonly<State>, id: string): (() => void) | null {
  return docked(s, id) ? null : () => actions.toggleBlock(id);
}

/* --------------------------- the field record ---------------------------

   The same record, for somebody standing under the bridge.

   EVERY BLOCK BELOW IS THE DESK SHELL'S OWN, REUSED WHOLE. What changes is the
   order, the chrome, and one substitution — the live 3D viewport becomes the
   still it was already sitting behind. Nothing here reimplements a panel, and if
   this file ever grows a second copy of the defects list or the inspection
   history, something has gone wrong: those two are the most heavily checked code
   on this screen and a field-only duplicate would drift from them silently.

   THE ORDER IS THE WORKFLOW. Actions first and pinned, because logging what you
   just saw is the reason the screen is open. Then what the structure is, then
   what it looks like, then what has been found on it before — history, defects,
   photographs. The inventory data comes last: it is reference material, and an
   inspector who needs the design load will scroll for it.
   --------------------------------------------------------------------------- */

/**
 * The two actions, pinned to the top of the scroller.
 *
 * Log defect is here and NOT in `recordActions` above, which is the desk shell's
 * set — the desk record reaches defect logging through the 3D view's toolbar and
 * the defects panel. On a phone the two things an inspector came to do have to
 * be the two things they can always see.
 *
 * What is deliberately absent: the inventory sheet (a print document, and nobody
 * is printing in a gorge), the model actions (desk work), and the Defects
 * inspector — that last one is the framed modeller, 1.6 MB from Cloudflare over a
 * district connection, and offering it here is exactly what the decision to
 * rebuild it in-app rejected. It returns with FA-06.
 */
function fieldActions(b: BridgeView, s: Readonly<State>): HTMLElement {
  /* WITHHELD FOR A ROLE THAT MAY NOT RECORD, and disabled rather than removed —
     the one place in this application where that is the right way round. These
     two buttons are the reason the screen exists and somebody who has used it
     before will look for them; an empty bar reads as a broken app, while a
     disabled button carrying the reason reads as an answer.

     A device with no signal is NOT this case. `can` returns true wherever the
     role is unknown, which includes a gorge — see its note in
     src/core/store.ts — because an inspector who cannot be told what they may do
     must not be stopped from working. */
  const mayInspect = can(s, 'inspection.log');
  const mayDefect = can(s, 'defect.log');

  return h(
    'div',
    { class: 'frec__actions' },
    button('Log overall inspection', {
      primary: true,
      grow: true,
      tall: true,
      iconPath: ICONS.plus,
      disabled: !mayInspect,
      title: mayInspect
        ? `Record an inspection of ${b.name}`
        : whyNot(s, 'Logging an inspection', 'inspection.log'),
      onClick: () => actions.openAddInspection(b.id),
    }),
    button('Log inspection', {
      grow: true,
      tall: true,
      iconPath: ICONS.plus,
      disabled: !mayDefect,
      title: mayDefect ? `Record an inspection on ${b.name}` : whyNot(s, 'Logging an inspection', 'defect.log'),
      onClick: () => actions.openLogDefect(b.id),
    }),
    /* THE DEFECT INSPECTOR, as its own page rather than a dialog.
       A link, not a button: it is a different document — src/inspector/main.ts —
       and navigating to it is what should happen. A button calling location.assign
       would look the same and lose the browser's own affordances, long-press
       included.
       Full width under the two, because it is a different KIND of action: those
       two record something now, this one opens a workspace. */
    h(
      'a',
      {
        class: 'btn btn--block btn--tall frec__inspect',
        href: `./inspector.html?bridge=${encodeURIComponent(b.id)}`,
        title: `Place issues on the 3D model of ${b.name}`,
      },
      icon(ICONS.inspect, { size: 14, width: 1.8 }),
      'Issue Inspector',
    ),
    /* Said once, under the two buttons, rather than left to a tooltip — which is
       the whole problem with a disabled control on a touchscreen: there is no
       hover, so the explanation would be unreachable on the device it is for. */
    !mayInspect || !mayDefect
      ? h('div', {
          class: 'frec__queued',
          text:
            `Your roles${s.identity?.roles ? ` (${rolesLabel(s.identity.roles)})` : ''} read this register ` +
            'rather than adding to it. A register administrator can change that on the People ' +
            'screen of the web app.',
        })
      : null,
    /* Queued writes are the one piece of the title bar the field shell loses, and
       this is the record where they are made. Shown only when there are any. */
    s.queued.length
      ? h('div', {
          class: 'frec__queued',
          text:
            `${s.queued.length} entr${s.queued.length === 1 ? 'y' : 'ies'} on this device ` +
            'not yet in the register — Options can send them.',
        })
      : null,
  );
}

/**
 * The model: its own geometry where there is any, and the still where there is not.
 *
 * THIS SHOWED NOTHING AT ALL, and the reason is worth writing down because the
 * card looked correct in every reading of it. It drew one thing — an `<img>` at
 * the still's URL — on the argument that a phone must not mount the modeller to
 * see what a picture already shows. Both halves of that stopped being true:
 *
 *   THE STILL IS NOT CAPTURED. Nothing mints one except an import, which treats a
 *   missed capture as a non-failure — the note above `hero` has the whole account,
 *   and the desk stopped gating on `hasThumb` for exactly this. Measured on the
 *   running server for BR-CHT-107: `hasThumb: false`, `hasGeometry: true`, and
 *   /api/modeler/thumb answering 404 while /api/modeler/geometry answers 200 with
 *   152 kB of glb. So the phone drew a broken image over a bridge the desk was
 *   rendering perfectly beside it.
 *
 *   AND THE 1.6 MB IS NOT WHAT DRAWS IT. A model with stored geometry is drawn by
 *   src/modeler/meshView.ts from that one file — no modeller, no parametric
 *   rebuild, nothing reconstructed. src/modeler/viewport.ts, which is the 1.6 MB,
 *   runs only for a model that has NO stored geometry (see the note in
 *   src/main.ts), so the slot below is gated on `hasGeometry` and the frame can
 *   never claim it. The phone gets the mesh; it still never gets the modeller.
 *
 * THE CLASS IS STILL THE POINT, just the other way round. `det-hero__live` is what
 * meshView's tracker looks for — it is the ONE selector it queries — so a card
 * that does not carry it gets no model however much geometry exists. That is what
 * was wrong here: the mesh view mounted, found no slot on this shell, and hid
 * itself every frame.
 *
 * The still stays as the fallback, and there `<img>` is right rather than a
 * background: it can carry alternative text and report its own failure. It is only
 * drawn when a still exists, so `onerror` now means what it says — the fetch
 * failed — instead of standing in for one that was never taken.
 */
function fieldHero(b: BridgeView, s: Readonly<State>): HTMLElement {
  const model = heroModel(modelsOf(b, s));
  const modelled = modelsOf(b, s).length;

  const gone = h('div', {
    class: 'frec__still-none',
    text: 'The saved still is not on this device, and there is no connection to fetch it.',
  });

  return h(
    'div',
    { class: 'det-block' },
    h(
      'div',
      { class: 'card__head', style: 'padding:0 0 10px' },
      h('span', { class: 'card__title', text: '3D model' }),
      h('span', { class: 'spacer' }),
      modelHint(s, model, modelled),
    ),
    model?.hasGeometry
      ? /* THE SLOT THE MESH VIEW DRAWS INTO. Empty on purpose: syncMeshView parks
           its own canvas over whatever carries PLACEHOLDER and measures this box
           every frame, so what belongs here is the space and nothing else. The
           height is the card's, not the canvas's — an element with no height is an
           element the tracker hides itself behind. */
        h('div', { class: ['frec__still', 'frec__still--mesh', PLACEHOLDER] })
      : model?.hasThumb
        ? h(
            'div',
            { class: 'frec__still' },
            h('img', {
              class: 'frec__still-img',
              src: api.modelThumbUrl(b.id, model.id, s.modelThumbNonce),
              alt: `Still of the saved 3D model of ${b.name}`,
              loading: 'lazy',
              decoding: 'async',
              onerror: (e: Event) => {
                const img = e.currentTarget as HTMLImageElement;
                img.replaceWith(gone);
              },
            }),
          )
        : h('div', {
            class: 'frec__still-none',
            text:
              modelled > 0
                ? 'A model is saved for this structure, but neither its geometry nor a still of it has reached this device.'
                : 'No 3D model has been built for this structure.',
          }),
    /* The two lines the artboard puts under the still: what the structure IS, and
       the three levels that decide whether it floods. An inspector arriving at a
       crossing reads those before anything else, and they were previously buried
       in the inventory facts further down the page. */
    h(
      'div',
      { class: 'frec__still-cap' },
      h('span', { text: geometryCaption(b) ?? 'Geometry not recorded' }),
      h('span', {
        /* Freeboard is computed and the two levels it comes from are not always
           recorded. Saying which is missing beats printing a dash. */
        text:
          b.freeboard != null
            ? `Deck RL ${b.deckRL!.toFixed(2)} m · HFL ${b.hfl!.toFixed(2)} m · freeboard ${b.freeboard.toFixed(2)} m`
            : 'Deck level or HFL not recorded',
      }),
      h('span', {
        class: 'frec__still-note',
        text: model?.hasGeometry
          ? 'The saved model, drawn from its own geometry. Log issue on its toolbar places an issue where you tap.'
          : 'A still of the saved model. Placing issues on the geometry needs the Issue Inspector.',
      }),
    ),
  );
}

/* --------------------------- the designed cards ---------------------------

   THE ARTBOARD'S OWN COMPOSITION, which is the part that was missing.

   Applying the design system re-coloured the desk panels the field record was
   reusing; it did not rebuild them, so the record was MD3-coloured and
   desk-composed. These three are what the "Bridge Record" artboard actually
   draws: a filled card for the model, and two outlined cards carrying only the
   inspection standing and the open defects.

   They are not replacements for the desk blocks — `conditionBlock` and
   `defectsBlock` still draw the full history and the component tree on the desk
   record, and the whole inventory is still below this on the field record. What
   these do is answer, in the first screenful, the three questions somebody
   standing under a bridge actually has: what is it, is it due, and what is
   already known to be wrong with it.
   --------------------------------------------------------------------------- */

/**
 * Inspection standing: both cycles as chips, then the most recent visit.
 *
 * BOTH cycles, because one chip could only ever show the worse of the two — which
 * is how a principal inspection six years late stayed invisible behind a routine
 * one done last month. Same reasoning as the desk record's own card, stated there
 * too; this is the two-chip version of it with the history list left off.
 */
function fieldHistoryCard(b: BridgeView): HTMLElement {
  const standing = inspectionStanding(b);
  const latest = standing.latest;
  const total = b.inspections.length;

  return h(
    'div',
    { class: 'frec__card' },
    h(
      'div',
      { class: 'frec__card-head' },
      h('span', { class: 'frec__card-title', text: 'Inspection history' }),
      h('span', {
        class: 'frec__card-hint',
        text: total ? `${total} on file` : 'None on file',
      }),
    ),
    h(
      'div',
      { class: 'frec__chips' },
      ...cycleChips(standing),
    ),
    latest
      ? h(
          'div',
          { class: 'frec__latest' },
          h(
            'div',
            { style: 'flex:1 1 auto;min-width:0' },
            h('div', {
              class: 'frec__latest-top',
              text: `${inspectionTypeLabel(latest.type)} · ${prettyDate(latest.date)}`,
            }),
            h('div', {
              class: 'frec__latest-sub',
              text: latest.note || 'No note recorded',
            }),
          ),
          /* At the scale the sheet used, never normalised: a 3 of 4 and a 7 of 10
             are different claims and rescaling them would be the register
             asserting something no inspector wrote down. */
          h(
            'div',
            { class: 'frec__rating' },
            h('span', { class: 'frec__rating-n', text: String(latest.rating) }),
            h('span', { class: 'frec__rating-of', text: `/${scaleMax(latest.scale)}` }),
          ),
        )
      : h('div', {
          class: 'frec__empty',
          text: 'No inspection has been recorded against this structure.',
        }),
    h(
      'div',
      { class: 'frec__card-foot' },
      button('Full history', {
        sm: true,
        title: 'Every inspection on file, with its element ratings and photographs',
        onClick: () => actions.toggleGroup('field-history'),
      }),
    ),
  );
}

/**
 * Open defects, worst first — and only the open ones.
 *
 * The desk record hangs every defect off the model's component tree, which is the
 * right answer at a desk and the wrong one in a hand: the tree is most of the
 * screen and a resolved defect from 2019 is not what somebody at the structure is
 * looking for. This is the open ones, ordered by severity, with the count of what
 * has been left out rather than a silent filter.
 */
function fieldDefectsCard(b: BridgeView, s: Readonly<State>): HTMLElement {
  const mine = s.registerDefects.filter((d) => d.bridgeId === b.id);
  const open = mine
    .filter((d) => isOpenStatus(d.status))
    .slice()
    .sort((x, y) => severityRank(y.severity) - severityRank(x.severity));
  const resolved = mine.length - open.length;

  return h(
    'div',
    { class: 'frec__card' },
    h(
      'div',
      { class: 'frec__card-head' },
      h('span', { class: 'frec__card-title', text: 'Inspection' }),
      h('span', {
        class: 'frec__card-hint',
        text: open.length ? `${open.length} open` : 'None open',
      }),
    ),
    open.length
      ? h(
          'div',
          { class: 'frec__defects' },
          ...open.map((d) =>
            h(
              'button',
              {
                type: 'button',
                class: 'frec__defect',
                title: `Open ${defectCodeLabel(d.code)} in the Issue Inspector`,
                onclick: () => {
                  window.location.href = `./inspector.html?bridge=${encodeURIComponent(b.id)}`;
                },
              },
              h('span', {
                class: 'frec__sev',
                style: { background: `var(--sev-${severityRank(d.severity)})` },
              }),
              h(
                'span',
                { class: 'frec__defect-main' },
                h('span', { class: 'frec__defect-top', text: defectCodeLabel(d.code) }),
                h('span', {
                  class: 'frec__defect-sub',
                  /* Never blank where a location belongs — the register's habit is
                     to say which of the two nothings it has. */
                  text: `${d.component ?? d.element ?? 'Not located'} · ${prettyDate(d.loggedAt)}`,
                }),
              ),
              tag(d.severity, severityRank(d.severity)),
            ),
          ),
        )
      : h('div', {
          class: 'frec__empty',
          /* Two different nothings, and which one it is matters: an unreachable
             register is not a claim that there are none. */
          text:
            s.backend !== 'live'
              ? 'The register could not be reached.'
              : 'No inspection is open on this structure.',
        }),
    resolved
      ? h('div', {
          class: 'frec__foot-note',
          text: `${resolved} resolved issue${resolved === 1 ? '' : 's'} not shown.`,
        })
      : null,
  );
}

/** The record, in the field shell. See the note above for the order. */
function fieldRecord(b: BridgeView, s: Readonly<State>): HTMLElement {
  return h(
    'div',
    { class: 'frec' },
    fieldActions(b, s),
    /* THE ARTBOARD'S ORDER, and it is the workflow: what the structure is, then
       whether it is due, then what is already wrong with it. The inventory facts
       used to sit between the actions and the model, which pushed the picture of
       the bridge below the fold on a phone. */
    fieldHero(b, s),
    fieldHistoryCard(b),
    fieldDefectsCard(b, s),
    /* Same position as on the desk record, for the same reason. An inspector at
       the structure is the person most likely to want to know whether what they
       just logged is minor work the Division can programme or a repair that
       needs the Bridge Unit. */
    remedialBlock(b, s),
    /* THE ONE CARD THE DESK RECORD GAVE UP THAT THIS ONE KEEPS, and it is kept
       deliberately. Recording a pour happens at the pour, and Construction
       Monitoring is a desk application with no field shell of its own — the
       field shell is Bridge Inspection whichever document opened it (see
       `appForShell`), so taking this card off this page would leave no way to
       record work from a site at all. The programme went: a baseline and a
       variance table are read in an office, and they are two taps away in the
       Scheduler for anybody at a desk. */
    worksBlock(b.id, b.name, s),
    photosBlock(b, s),
    /* Reference material, below the fold and in the order it gets asked for.
       Screening leads it because flood and scour are the two facets an inspector
       at a riverbank can actually corroborate; the inventory facts follow. */
    screeningBlock(b, s),
    stateBlock(b, s),
    detailsBlock(b, s),
    structureBlock(b, s),
    constructionCard(b, s),
    consequenceCard(b, s),
    historyCard(b, s),
  );
}

/**
 * The splitter between the inventory and the model: drag it, double-click it to
 * unpin the panel, or focus it and use the arrow keys — Home and End go to the
 * narrowest and widest. A `separator` with a value, so a screen reader reads
 * the width as it changes.
 */
function splitter(width: number): HTMLElement {
  return h('div', {
    class: 'det-resize',
    role: 'separator',
    tabindex: '0',
    'aria-orientation': 'vertical',
    'aria-label': 'Resize the inventory panel',
    'aria-valuemin': String(MIN_PANEL_W),
    'aria-valuemax': String(MAX_PANEL_W),
    'aria-valuenow': String(width),
    title: 'Drag to resize · double-click to collapse',
    onpointerdown: (e: PointerEvent) => beginResize(e, width),
    ondblclick: () => actions.toggleDetailPanel(),
    onkeydown: (e: KeyboardEvent) => {
      const step = e.shiftKey ? KEY_STEP * 4 : KEY_STEP;
      const to =
        e.key === 'ArrowLeft'
          ? width - step
          : e.key === 'ArrowRight'
            ? width + step
            : e.key === 'Home'
              ? MIN_PANEL_W
              : e.key === 'End'
                ? MAX_PANEL_W
                : null;
      if (to === null) return;
      e.preventDefault();
      actions.setDetailPanelWidth(Math.min(MAX_PANEL_W, Math.max(MIN_PANEL_W, to)));
      /* The tree is rebuilt on every store update (see src/main.ts), so the
         element that had focus is gone; give it to the new one so the next
         press lands. */
      requestAnimationFrame(() => document.querySelector<HTMLElement>('.det-resize')?.focus());
    },
  });
}

/**
 * Drags the details column's width by writing the `--det-w` custom property
 * directly on the live `.det-cols` element, bypassing the store until
 * pointerup. The whole app tree is rebuilt from scratch on every store
 * update (see src/main.ts), so routing every pointermove through it would
 * rebuild everything at pointer-move frequency; only the final width is
 * committed, in one store update, when the drag ends.
 *
 * The column being resized is the FIRST one, so dragging right widens it.
 * Pulled well past the minimum it unpins instead — see SNAP_SHUT_PX.
 */
function beginResize(e: PointerEvent, startWidth: number): void {
  if (e.button !== 0) return;
  e.preventDefault();
  const handle = e.currentTarget as HTMLElement;
  const cols = handle.closest<HTMLElement>('.det-cols');
  if (!cols) return;
  const startX = e.clientX;
  let raw = startWidth;

  /* The whole page takes the resize cursor and stops selecting text while the
     drag is on, or the pointer leaving the 6px handle flickers it back to an
     arrow and paints a selection across the cards. */
  document.body.classList.add('is-col-resizing');
  handle.classList.add('is-dragging');

  const onMove = (ev: PointerEvent): void => {
    raw = startWidth + (ev.clientX - startX);
    const w = Math.min(MAX_PANEL_W, Math.max(MIN_PANEL_W, raw));
    cols.style.setProperty('--det-w', `${w}px`);
    cols.classList.toggle('det-cols--closing', raw < MIN_PANEL_W - SNAP_SHUT_PX);
  };
  const onUp = (): void => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onUp);
    document.body.classList.remove('is-col-resizing');
    handle.classList.remove('is-dragging');
    cols.classList.remove('det-cols--closing');
    if (raw < MIN_PANEL_W - SNAP_SHUT_PX) {
      actions.toggleDetailPanel();
      return;
    }
    const w = parseFloat(cols.style.getPropertyValue('--det-w'));
    actions.setDetailPanelWidth(Number.isFinite(w) ? w : startWidth);
  };
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onUp);
}

/**
 * The property panel, with one header: a page drawn as a single block has that
 * block's heading lifted into the panel's — its count or name as the meta, its
 * buttons and chips beside the pin — rather than a second heading under the first.
 */
function propertyPanel(b: BridgeView, s: Readonly<State>, entry: ExplorerEntry): HTMLElement {
  /* Monitoring's site pages draw the planned activity here, whatever page is picked. */
  const title = monitoring(s) && SITE_VIEWS.has(entry.view) ? PLANNED_TITLE : entry.label;
  const body = propertyBody(b, s, entry.view);
  const lifted = liftHead(body, title);
  return panel(
    {
      title,
      ...(lifted.meta !== undefined ? { meta: lifted.meta } : {}),
      actions: [...lifted.actions, ...propertyActions(b, s, entry.view)],
      ...(entry.view === 'baseline' ? { foot: baselineFoot(s) } : {}),
      class: 'det-inv',
    },
    ...body,
  );
}

/**
 * Takes the period report's period bar out of its body, for the panel's head:
 * its buttons drawn as the head's, and its Print left behind, since the head
 * prints already.
 */
function liftPeriodBar(body: HTMLElement): HTMLElement | null {
  const bar = body.querySelector<HTMLElement>('.wrep__bar');
  if (bar === null) return null;
  bar.querySelector('.wrep__print')?.remove();
  for (const el of bar.querySelectorAll<HTMLElement>('.btn')) {
    el.classList.remove(...[...el.classList].filter((c) => c === 'btn' || c.startsWith('btn--')));
    el.classList.add('panel__btn', 'panel__btn--text');
  }
  bar.classList.add('wrep__bar--head');
  bar.remove();
  return bar;
}

/** Takes a lone docked block's heading off it, and returns what it carried. */
function liftHead(body: HTMLElement[], title: string): { meta?: string; actions: HTMLElement[] } {
  const head =
    body.length === 1
      ? [...body[0]!.children].find((el) => el.classList.contains('card__head--fold'))
      : undefined;
  if (head === undefined || ![...head.children].some((el) => el.classList.contains('fold__btn--fixed'))) {
    return { actions: [] };
  }
  const name = head.querySelector('.card__title')?.textContent ?? '';
  const hint = head.querySelector('.card__hint')?.textContent ?? '';
  const meta = [name === title ? '' : name, hint].filter(Boolean).join(' · ');
  const actions = [...head.children].filter(
    (el): el is HTMLElement =>
      el instanceof HTMLElement && !el.matches('.fold__btn, .spacer'),
  );
  for (const el of actions) {
    if (!el.classList.contains('btn')) continue;
    el.classList.remove(...[...el.classList].filter((c) => c === 'btn' || c.startsWith('btn--')));
    el.classList.add('panel__btn', 'panel__btn--text');
  }
  head.remove();
  return meta ? { meta, actions } : { actions };
}

/**
 * The property panel's header controls: the edit action, on the inventory page,
 * and the pushpin that unpins it to the rail.
 *
 * Correcting a transcribed inventory record is desk work with the drawings to
 * hand — `inventory.edit` — so an inspector gets no edit button, and the header
 * is the name and the pin alone.
 */
function propertyActions(b: BridgeView, s: Readonly<State>, view: DetailView): HTMLElement[] {
  const mayEdit = view === 'inventory' && can(s, 'inventory.edit');
  return [
    ...(view === 'baseline' ? baselineHeadActions(s, b.name) : []),
    ...(mayEdit
      ? [
          panelButton({
            icon: ICONS.edit,
            text: 'Edit details',
            title: 'Correct or fill in this inventory record',
            onclick: () => actions.openEdit(b.id),
          }),
        ]
      : []),
    panelButton({
      icon: ICONS.pin,
      title: 'Auto Hide — collapse to the side',
      label: 'Collapse the property panel',
      onclick: () => actions.toggleDetailPanel(),
    }),
  ];
}

/* --------------------------- screening --------------------------- */

/**
 * One input a band was computed from.
 *
 * The value is set apart when the register does not hold it, in the same muted
 * style `fact` uses, because on today's records the missing inputs ARE the
 * finding: three of five structures cannot be screened for scour or seismic and
 * this is where you see which column would fix that.
 */
function screeningInput(input: ScreeningFacet['inputs'][number]): HTMLElement {
  return h(
    'div',
    {},
    h(
      'div',
      { class: ['scr__in', !input.known && 'scr__in--none'] },
      h('span', { class: 'scr__in-k', text: input.label }),
      h('span', { class: 'scr__in-v', text: input.value }),
    ),
  );
}

/**
 * One of the three screens, with the sentence that justifies it and every input
 * it read.
 *
 * The reason and the inputs are not a tooltip and not behind a disclosure. A
 * screening band is a number somebody will act on, and a band nobody can audit
 * is one they will either over-trust or ignore — both worse than four short
 * lines of arithmetic they can check against the sheet in their hand.
 */
function screeningFacet(facet: ScreeningFacet): HTMLElement {
  return h(
    'div',
    { class: 'scr__facet' },
    h(
      'div',
      { class: 'scr__head' },
      h('span', { class: 'scr__name', text: facet.label }),
      h('span', { class: 'spacer' }),
      tag(bandLabel(facet.band), bandRank(facet.band)),
    ),
    h('div', { class: 'scr__why', text: facet.reason }),
    h('div', { class: 'scr__inputs' }, ...facet.inputs.map(screeningInput)),
  );
}

/**
 * Flood, scour and seismic screening for this structure.
 *
 * A SCREEN, not an assessment, and the card head says so where somebody reading
 * over a shoulder will see it. See src/data/screening.ts for what each band
 * turns on, what is deliberately not screened, and why a missing input scores
 * "not screened" rather than "low".
 */
function screeningBlock(b: BridgeView, s: Readonly<State>): HTMLElement {
  const screening = screenBridge(b);
  const unscreened = screening.facets.filter((f) => f.band === 'unknown');

  const blockIsOpen = blockOpen(s, 'screening');
  const head = foldHead(blockIsOpen, foldToggle(s, 'screening'),
      'Hazard screening',
      /* Which of the three have nothing to screen on, since that is the state
         this card is in on most records and the reason to open it. The three
         names are inside, one per facet; repeating them in the heading was what
         made this hint too long to read at the width it has. */
      unscreened.length === 0
        ? 'All three screened'
        : `${unscreened.map((f) => f.label).join(' and ')} not screened`,
      tag(
        bandLabel(screening.overall),
        bandRank(screening.overall),
        'Worst of the three',
      ),
    );
  if (!blockIsOpen) return shutBlock(head);

  return h(
    'div',
    { class: 'det-block det-block--flush' },
    head,
    h('div', { class: 'scr' }, ...screening.facets.map(screeningFacet)),
  );
}

/* --------------------------- foldable blocks --------------------------- */

/*
   EVERY BLOCK ON THE RECORD IS CLOSED UNTIL SOMEBODY OPENS IT, and that is the
   answer to what this page had become. Nine blocks, and on a register being
   filled in most of them are empty on most structures: no inspections, no
   defects, no photographs, a screening that says "not screened" three times. The
   record opened as a column of headings over empty states, and the two or three
   that had anything in them were somewhere below the fold.

   So the record opens as its own contents page. Each heading carries the count
   or the state of what is inside it — "2 open of 2", "none on file", "all three
   screened" — so a closed record still says which blocks are worth opening, and
   the eye runs down nine short lines instead of nine screens.

   WHAT IS OPEN IS PERSISTED, so this is a habit and not a setting: somebody who
   reads the defect log on every structure opens it once and it stays open on the
   next record, and on the one after that. See `openBlocks` in
   src/data/types.ts, which is a list of what is OPEN because the default is
   shut — the opposite of `collapsedGroups` below it.

   THE 3D MODEL IS THE EXCEPTION and is not foldable at all. It is not a record
   about the structure, it is the structure; a bridge record whose bridge is
   behind a disclosure triangle is a filing cabinet.
*/

/**
 * Every foldable block on the record, in the order the page draws them.
 *
 * Exported because two things outside this file need the list and neither should
 * keep its own copy: `npm test`, which opens them all to check what is inside
 * them, and anything that ever offers "open everything". The 3D model is not in
 * it — see the note above.
 */
export const RECORD_BLOCKS: readonly string[] = [
  'inspections',
  'screening',
  'defects',
  'remedial',
  'changes',
  'photos',
];

/**
 * Whether a row of a group holds nothing.
 *
 * A row is usually the fact itself, and `fact` marks it. Consequence of closure
 * wraps each one with the note that says WHY there is no value — "nobody has
 * assessed this", "the count is fifteen years old" — so there the mark is one
 * level down. Both shapes are read here rather than at the two call sites,
 * because a rule that only knew about the first quietly printed four em dashes
 * over a switch that said it was hiding them.
 */
function rowIsEmpty(row: HTMLElement): boolean {
  if (row.getAttribute('data-empty') === '1') return true;
  const facts = [...row.querySelectorAll('.fact')];
  return facts.length > 0 && facts.every((f) => f.getAttribute('data-empty') === '1');
}

/** Whether this reader has opened that block. */
function blockOpen(s: Readonly<State>, id: string): boolean {
  return docked(s, id) || s.openBlocks.includes(id);
}

/**
 * A closed block: its heading, and nothing else.
 *
 * The body is not built at all — every one of these returns early — so a
 * collapsed defect log costs neither the component tree it would flatten nor
 * the rows it would draw.
 */
function shutBlock(head: HTMLElement): HTMLElement {
  return h('div', { class: 'det-block det-block--flush det-block--shut' }, head);
}

/* --------------------------- collapsible section --------------------------- */

/**
 * A group of the inventory panel, and what it does with the fields nothing
 * records.
 *
 * THE PROBLEM THIS SOLVES IS NOT COSMETIC. Half of this register's fields are
 * empty on a typical structure — a transcription off a general-arrangement
 * drawing carries a deck level and a span count and says nothing about design
 * load, bank protection or a height restriction — so a panel of eleven rows
 * printed two values and nine em dashes, and the two were the ones being
 * scrolled past.
 *
 * The empty rows are left out. A group with nothing recorded at all says so
 * — "Nothing recorded here" — rather than drawing a heading over nothing.
 * There is no switch to bring them back on this screen; the edit form lists
 * every field, recorded or not, and that is where somebody filling a record
 * in sees them.
 */
function section(
  s: Readonly<State>,
  name: string,
  title: string,
  hint: string | undefined,
  rows: HTMLElement[],
  /* `grid` lays the facts out label over value, as many columns as the panel
     has room for; `aside` is drawn at the right of the heading. */
  opts: { grid?: boolean; aside?: Child } = {},
): HTMLElement {
  const collapsed = s.collapsedGroups.includes(name);
  /* `data-empty` is set by `fact` itself, where the em dash is decided — see
     src/components/widgets.ts. Read here rather than re-derived, so the two can
     never disagree about what counts as unrecorded. */
  const empty = rows.filter(rowIsEmpty);
  const shown = rows.filter((r) => !empty.includes(r));
  return h(
    'div',
    { class: 'det-block det-block--flush', 'data-group': name },
    h(
      'button',
      {
        type: 'button',
        class: 'grp__head',
        'aria-expanded': String(!collapsed),
        onclick: () => actions.toggleGroup(name),
      },
      icon(ICONS.chevronDown, {
        size: 10,
        width: 2,
        class: collapsed ? 'grp__chev grp__chev--closed' : 'grp__chev',
      }),
      h('span', { class: 'grp__title', text: hint ? `${title} · ${hint}` : title }),
      opts.aside ?? null,
    ),
    collapsed
      ? null
      : h(
          'div',
          { class: ['det-facts', opts.grid && 'det-facts--grid'], style: 'padding:2px 14px 12px' },
          ...shown,
          /* A group with nothing recorded says so, rather than drawing a
             heading over nothing. */
          shown.length === 0
            ? h('div', { class: 'det-facts__hidden', text: 'Nothing recorded.' })
            : null,
        ),
  );
}

/* --------------------------- identity --------------------------- */

/**
 * The hero: a live, orbitable 3D viewport if a model exists, empty otherwise.
 *
 * What is rendered here is only a PLACEHOLDER. The viewport is the modeller's own
 * Canvas3D in an iframe, and an iframe cannot live inside the render root: moving
 * one in the DOM reloads it, restarting a 1.6 MB download and losing the user's
 * camera angle, and no node in a reconciled tree is guaranteed to stay put. So
 * src/modeler/viewport.ts parks the frame in a container on document.body and
 * positions it over this box — and crops it to whatever part of this box is on
 * show, since a fixed frame is clipped by neither the panel nor the pane. The
 * measured element is why the placeholder still has to occupy the space.
 *
 * The still is not wasted: it is the poster behind the frame, so the panel shows the
 * model while the workspace chunk loads rather than an empty rectangle.
 *
 * WHAT GATES IT IS `heroModel`, NOT `hasThumb`. It was `hasThumb` — the still being
 * a fair proxy for "built and rendered at least once", false only for the first few
 * seconds of a model's life — and both halves of that stopped being true: the
 * register mints an empty model before an import, and nothing captures a still
 * except that import, which treats a missed capture as a non-failure. So a still
 * that never arrived meant this box showed nothing forever over a saved
 * bridge. derive.ts has the whole account.
 *
 * The empty box is the fallback rather than an error state: a structure nobody
 * has imported to has no model, which is ordinary.
 */
function hero(b: BridgeView, s: Readonly<State>): HTMLElement {
  const model = heroModel(modelsOf(b, s));

  /* An import unmounts the live viewport — two modellers on one model would
     race each other's saves, see src/modeler/viewport.ts — so for those few
     seconds this box IS the poster, and it says what is happening to it rather
     than sitting there looking like a viewport that failed to load. */
  const importing = s.modelImport?.bridgeId === b.id ? s.modelImport : null;

  /* AND IT SAYS SO WITH NO MODEL AT ALL, which is the case that had nothing.
     A first import has no still to be a poster and nothing to unmount, so this
     used to fall through to the elevation-photo drop slot — a click on "Import
     3D model" put a photo placeholder on screen and left it there for the ten
     seconds the import took. */
  /* WHETHER THE GEOMETRY CAN BE DRAWN HERE AT ALL — see meshAvailability. A
     modeller that cannot find an imported mesh rebuilds the bridge from its
     parameters and warns in an alert this sandbox swallows, so anything other than
     `'ready'` must not get a frame.

     NONE OF WHICH APPLIES TO A MODEL WITH STORED GEOMETRY, and that is the point of
     `rendered`. Its `.glb` is one object in the bucket — the source's own triangles,
     derived from the export — and src/modeler/meshView.ts draws it directly. No
     modeller, so nothing that could rebuild the bridge; no device-local mesh, so
     nothing to be missing. The question meshAvailability answers cannot arise, and
     asking it anyway would hide a perfectly good bridge behind a still. */
  const rendered = model?.hasGeometry === true;
  const mesh = model && !rendered ? meshAvailability(model, s.modelMeshState) : 'ready';

  if (!model) {
    /* NOTHING MODELLED AND NOTHING DROPPED, WHICH IS MOST OF A NEW REGISTER —
       and until this line that state reserved 420 pixels at the top of the
       record to say so. It was the largest thing on the page and the emptiest,
       above every block that had something in it.

       Empty, with no photo drop slot: the panel header already says "No 3D
       model built". An import in flight keeps the full height — that one has
       something to show, a ring and a caption that move. */
    return h(
      'div',
      { class: ['det-hero', !importing && 'det-hero--empty'] },
      importing ? importPanel(importing) : null,
    );
  }

  /* NO MESH HERE, BUT KEEP THE STILL. The still was photographed from the real
     geometry at import, so it is a true picture of this bridge and the best thing
     available — far better than the frame,
     which would draw a parametric approximation of it. What it does not get is the
     progress sweep or the live slot: nothing is loading, and the caption above says
     why. Where there is no still either, the space is left empty. */
  if (mesh === 'absent') {
    return h(
      'div',
      { class: 'det-hero' },
      importing
        ? importPanel(importing)
        : model.hasThumb
          ? h('div', {
              class: 'det-hero__still',
              style: {
                backgroundImage: `url("${api.modelThumbUrl(b.id, model.id, s.modelThumbNonce)}")`,
              },
              role: 'img',
              'aria-label': `Saved still of ${model.name}. Its 3D geometry is not on this device.`,
            })
          : null,
    );
  }

  return h(
    'div',
    { class: 'det-hero' },
    h(
      'div',
      {
        class: PLACEHOLDER,
        /* The still, as this box's background. It shows immediately, the live frame
           covers it once its 1.6 MB chunk has loaded and painted (the embedded page
           is transparent until then), and because it is a background on an element
           inside the render root it also appears in a print view and in the design
           snapshots — neither of which can show an iframe. One copy, three uses.

           The nonce is what makes an import's new still replace this one: the URL
           is otherwise identical before and after — see api.modelThumbUrl.

           OMITTED ENTIRELY for a model with no still, which since heroModel stopped
           requiring one is a case that reaches here. GET /api/modeler/thumb 404s
           that, and a background-image pointing at a 404 is a request made on every
           paint for an answer already known — and in a print view, where no frame
           can cover it, an empty rectangle either way. */
        style: model.hasThumb
          ? { backgroundImage: `url("${api.modelThumbUrl(b.id, model.id, s.modelThumbNonce)}")` }
          : {},
      },
      /* Shows through the frame while it is still transparent, same as the poster —
         see src/modeler/viewport.ts's wireViewportMessages for what flips this off.
         Also drawn while the mesh check is outstanding, which is the one case where
         something really is pending and no frame has been asked for yet. */
      s.heroViewportReady && !importing && mesh === 'ready'
        ? null
        : h(
            'div',
            { class: 'det-hero__progress', 'aria-hidden': 'true' },
            h('div', { class: 'det-hero__progress-bar' }),
          ),
      importing
        ? h(
            'div',
            { class: 'det-hero__busy' },
            h('span', {
              class: 'det-hero__busy-title',
              text: importProgress(importing).label,
            }),
            h('span', {
              class: 'det-hero__busy-sub',
              text: importCaption(importing),
            }),
          )
        : null,
    ),
  );
}

/**
 * What the busy line says under the phase, model named once there is one.
 *
 * "The modeller is doing this in the background" is only true for the phases that
 * involve the hidden frame. Reading the chosen file is this device, and creating a
 * model is one request to the register's own API — saying the modeller was busy
 * during either would credit the wrong thing for the wait, and the wait is the
 * whole reason this line exists.
 */
function importCaption(run: ModelImport): string {
  const at = importProgress(run);
  const where = run.modelName ? `${run.modelName} · ` : '';
  /* A RESTORE SAYS WHOSE DOING IT IS. Nobody chose a file: the app found that this
     device has no triangles for a model whose state says it displays some, and is
     replaying the export the server holds. "Importing…" over a record the reader
     has not touched reads as the register having decided to change something, so
     the caption names the actual situation instead. */
  const who = run.restoring
    ? 'this device did not have the model — fetching it from the server'
    : run.phase === 'reading' || run.phase === 'creating'
      ? 'preparing the import'
      : run.phase === 'storing'
        ? 'putting the export on the server before anything is changed'
        : run.phase === 'geometry'
          ? 'so the bridge shows on every device, not just this one'
          : 'the modeller is doing this in the background';
  return `${where}step ${at.step} of ${at.of} · ${who}`;
}

/**
 * The whole panel, for a structure with no still to draw one over.
 *
 * A stepped bar rather than the indeterminate sweep the poster uses: with no
 * model behind it there is nothing else on screen saying anything, so this is
 * where the countable half of the progress is worth showing as a width.
 */
function importPanel(run: ModelImport): HTMLElement {
  const at = importProgress(run);
  return h(
    'div',
    {
      class: 'imp-panel',
      role: 'status',
      'aria-live': 'polite',
      'aria-label': `${at.label} step ${at.step} of ${at.of}`,
    },
    importRing(),
    h('div', { class: 'imp-panel__title', text: at.label }),
    h(
      'div',
      { class: 'imp-panel__track', 'aria-hidden': 'true' },
      h('div', {
        class: 'imp-panel__fill',
        // A real fraction of a real sequence — see importProgress.
        style: { width: `${Math.round((at.step / at.of) * 100)}%` },
      }),
    ),
    h('div', { class: 'imp-panel__sub', text: importCaption(run) }),
  );
}

/** A fact that takes the whole width of a grid group — a sentence rather than a figure. */
function wide(el: HTMLElement): HTMLElement {
  el.classList.add('fact--wide');
  return el;
}

/**
 * The head of Bridge info: what the structure is called and the
 * four figures asked for first. Every figure here is in a group below as well;
 * a figure nobody recorded is left out of the strip rather than drawn as a dash.
 */
function inventorySummary(b: BridgeView): HTMLElement {
  const figures = [
    { k: 'Total length', v: b.length, digits: 2, unit: 'm' },
    { k: 'Spans', v: b.spans, digits: 0, unit: '' },
    { k: 'Deck width', v: b.deckWidth, digits: 1, unit: 'm' },
    { k: 'Freeboard', v: b.freeboard, digits: 2, unit: 'm' },
  ].filter((f) => f.v != null && Number.isFinite(f.v));
  return h(
    'div',
    { class: 'inv-sum' },
    h(
      'div',
      { class: 'inv-sum__top' },
      h(
        'div',
        { class: 'inv-sum__id' },
        h('div', { class: 'inv-sum__name', text: b.name }),
      ),
      h('span', { class: 'reg__state' }, phaseChip(b), worksChip(b)),
    ),
    figures.length
      ? h(
          'div',
          { class: 'inv-sum__figs' },
          ...figures.map((f) =>
            h(
              'div',
              { class: 'inv-sum__fig' },
              h('span', { class: 'inv-sum__k', text: f.k }),
              h(
                'span',
                { class: 'inv-sum__v' },
                f.v!.toFixed(f.digits),
                f.unit ? h('span', { class: 'inv-sum__u', text: ` ${f.unit}` }) : null,
              ),
            ),
          ),
        )
      : null,
  );
}

/** Where the structure is. Collapsible like the other groups below it. No hero — see heroBlock. */
function detailsBlock(b: BridgeView, s: Readonly<State>): HTMLElement {
  return section(s, 'details', 'Location', undefined, [
    /* Labelled in the tenant's own words — District/Province at DoR, the
       District Engineering Office's district and Region at DPWH — because these
       rows exist to match the paper sheet somebody is holding beside the screen.
       See `vocab` in src/tenant/types.ts. */
    fact('Road', join([b.route, b.roadName], ' · ')),
    fact(vocab().chainage, chainage(b.km) ?? NOT_RECORDED),
    fact(vocab().area, shown(b.district)),
    fact(vocab().region, shown(b.province)),
    fact('Crosses', shown(b.river)),
    ...generalFacts(b, s),
  ], { grid: true });
}

/** What the structure's model says about it, where it has been read for this structure. */
function modelOf(b: BridgeView, s: Readonly<State>): State['modelInventory'] {
  return s.modelInventory?.bridgeId === b.id ? s.modelInventory : null;
}

/** The record with the model's spans, lengths, deck width and superstructure over its own. */
function asModelled(b: BridgeView, s: Readonly<State>): BridgeView {
  const m = modelOf(b, s);
  if (m === null) return b;
  const v = m.fill.values;
  const num = (x: number | string | undefined, held: number | null) => (typeof x === 'number' ? x : held);
  return {
    ...b,
    spans: num(v.spans, b.spans),
    length: num(v.length, b.length),
    spanLength: num(v.spanLength, b.spanLength),
    deckWidth: num(v.deckWidth, b.deckWidth),
    type: m.fill.superstructure ?? b.type,
  };
}

function modelMark(modelId: string): { mark: string; label: string; note: string } {
  return { mark: 'M', label: 'Bridge model', note: `Read from the bridge model ${modelId}.` };
}

/**
 * What the structure is and how high it stands over the water. The levels are
 * drawn as well as listed where both are recorded: deck over flood level, with
 * the freeboard between them.
 */
function structureBlock(record: BridgeView, s: Readonly<State>): HTMLElement {
  const m = modelOf(record, s);
  const b = asModelled(record, s);
  const freeboard = b.freeboard ?? (b.deckRL != null && b.hfl != null ? b.deckRL - b.hfl : null);
  /* The model's value where it states one, marked as the model's; the record's otherwise. */
  const either = (field: 'spans' | 'length' | 'deckWidth', value: string): Child =>
    m?.fill.values[field] != null ? sourced(value, modelMark(m.modelId)) : atHandover(b, s, field, value);
  const girders = m?.fill.values.girders;
  return section(s, 'structure', 'Structure & levels', undefined, [
    fact('Superstructure', m?.fill.superstructure ? sourced(m.fill.superstructure, modelMark(m.modelId)) : shown(b.type)),
    fact('Spans', b.spans == null ? NOT_RECORDED : either('spans', String(b.spans))),
    fact('Total length', either('length', metres(b.length))),
    ...(typeof girders === 'number' ? [fact('Girders', sourced(String(girders), modelMark(m!.modelId)))] : []),
    fact('Deck width', either('deckWidth', metres(b.deckWidth, 1))),
    fact('Carriageway', metres(b.carriageway, 1)),
    fact('Year built', count(b.year)),
    fact('Deck level', b.deckRL != null ? `${b.deckRL.toFixed(2)} m` : '—'),
    fact('HFL', b.hfl != null ? `${b.hfl.toFixed(2)} m` : '—'),
    fact('Freeboard', b.freeboard != null ? `${b.freeboard.toFixed(2)} m` : '—'),
    fact(
      'Load restriction',
      b.loadRestriction != null ? sourced(`${b.loadRestriction} T`, provenanceOf(b, 'loadRestriction', s.changes)) : '—',
    ),
    fact('Height restriction', b.heightRestriction != null ? `${b.heightRestriction} m` : '—'),
    wide(fact('Design load', b.designLoad ?? '—')),
    ...(b.deckRL != null && b.hfl != null && freeboard != null
      ? [
          h(
            'figure',
            {
              class: 'lvl',
              'aria-label': `Deck level ${b.deckRL.toFixed(2)} m, HFL ${b.hfl.toFixed(2)} m, freeboard ${freeboard.toFixed(2)} m`,
            },
            h('div', { class: 'lvl__row' }, h('span', { text: 'Deck level' }), h('strong', { text: `${b.deckRL.toFixed(2)} m` })),
            h('div', { class: 'lvl__deck' }),
            h('div', { class: 'lvl__gap' }, h('span', { class: 'lvl__tick' }), h('span', { text: `Freeboard ${freeboard.toFixed(2)} m` })),
            h('div', { class: 'lvl__hfl' }),
            h('div', { class: 'lvl__row' }, h('span', { text: 'HFL' }), h('strong', { text: `${b.hfl.toFixed(2)} m` })),
            h('div', { class: 'lvl__water' }),
          ),
        ]
      : []),
  ], { grid: true });
}

/**
 * The only model action there is: bring a CSiBridge export onto this record.
 *
 * IT USED TO BE TWO, and the second was "Build 3D model" — a template picker
 * that sent the user into the modeller to start a bridge from parameters. A model
 * is only ever imported now, so there is nothing to build and nowhere to be sent:
 * the modeller is not a destination, it is the parser the import runs off screen
 * (src/modeler/importModel.ts) and the canvas the record renders in.
 *
 * WHICH MEANS THIS DOES NOT DEPEND ON A MODEL EXISTING. On a structure nobody
 * has imported to yet, the import creates the model it lands in — see the note
 * on importBridgeModel.
 *
 * Exported for the Inspection ribbon's Record group, which is where it is drawn
 * now that the record has no header of its own. Returned as the parts of a
 * command rather than as a button, so the ribbon draws it like its neighbours.
 * Null means there is no record in hand to import onto.
 */
export function modelImportCommand(
  s: Readonly<State>,
): { label: string; title: string; busy: boolean; run: () => void } | null {
  const b = findBridge(s.selectedId);
  if (b === null) return null;

  const running = s.modelImport;
  if (running && running.bridgeId === b.id) {
    const at = importProgress(running);
    return {
      label: `${at.label} (${at.step}/${at.of})`,
      title: running.modelName
        ? `Importing into "${running.modelName}" — this runs in the background and saves to the Cloud`
        : 'Importing — this runs in the background and saves to the Cloud',
      busy: true,
      run: () => {},
    };
  }

  /* The model the hero viewport is showing — see hero() — so the import lands on
     exactly what is on screen. Null means nothing has been imported here yet, and
     the import makes one; it is deliberately NOT a reason to disable the command,
     because a first import is the common case on this register. The extra
     fallback: an empty shell from an import that did not finish is exactly the
     model the NEXT import should land in, rather than minting a second one. */
  const models = s.models.models;
  const target = heroModel(models) ?? models[0] ?? null;

  return {
    label: 'Import 3D model',
    title: target
      ? `Import a CSiBridge export (Components bundle .json, optionally with an ` +
        `alignment file) into "${target.name}" — without leaving this record`
      : `Import a CSiBridge export (Components bundle .json, optionally with an ` +
        `alignment file) — the model is created for it, without leaving this record`,
    busy: false,
    run: () => importBridgeModel(b.id, target, b.name),
  };
}

/**
 * "Delete 3D model" on the ribbon, and the only way to remove a model.
 *
 * IT USED TO BE NOWHERE, and that was a real hole rather than a decision. The
 * confirmation, its actions and the `DELETE /api/modeler/models` endpoint were all
 * intact and tested; what opened them was the modeller's own dashboard, reached
 * with `m`. Removing the modeller as a destination took the dashboard with it and
 * left the machinery unreachable — a record could acquire models and never let go
 * of one. This is that control, on the ribbon beside Import 3D model, where the
 * rest of the model's actions are.
 *
 * NOT SHOWN WITH NOTHING TO DELETE, and not while an import is running: an import
 * is several writes to the very keys a delete removes, and the two racing would
 * leave a model half-gone with no way to tell which half. The import is seconds
 * and the button comes back after it.
 *
 * `backend === 'live'` because the confirmation lists what is saved, and a list
 * that could not be read is not a list to delete from — the same reason the
 * panel's caption refuses to claim there are no models when it cannot tell.
 */
export function modelDeleteCommand(
  s: Readonly<State>,
): { title: string; run: () => void } | null {
  const b = findBridge(s.selectedId);
  if (b === null || s.screen !== 'board') return null;
  /* `model.manage`, first: deleting the geometry every other screen draws from is
     not field work, and this is the only control in the application that can do
     it. */
  if (!can(s, 'model.manage')) return null;
  if (s.backend !== 'live' || s.models.models.length === 0) return null;
  if (s.modelImport?.bridgeId === b.id) return null;

  return {
    title:
      s.models.models.length === 1
        ? `Delete "${s.models.models[0]!.name}" from ${b.id}`
        : `Delete one of the ${s.models.models.length} models saved against ${b.id}`,
    run: () => actions.openModelDelete(b.id),
  };
}

/**
 * What the panel says it is showing.
 *
 * The unreachable case is written out rather than left to a tooltip: an empty
 * model index and an unreadable one look identical, and only one of them means
 * the engineer's work is not there. Absence of evidence is not evidence of
 * absence, and the panel has to say which it has.
 */
function modelHint(
  s: Readonly<State>,
  /* The whole summary rather than three fields of it. It was a structural type
     until this had to reason about the mesh as well — at which point every question
     the caption answers is one the index already carries, and picking them out
     one by one only meant the type had to be edited to ask a new one. */
  model: ModelSummary | null | undefined,
  modelled: number,
): HTMLElement | null {
  /* A model we can see beats anything we can say about the index — it is on
     screen, so whether the index is trustworthy is beside the point. Its name is
     in the status bar (src/components/structurePane.ts). */
  if (model) {
    /* THE MESH IS SOMEWHERE ELSE, and no amount of waiting brings it here. The
       triangles of an imported model live in the importing browser's IndexedDB —
       which is scoped to the origin, so another machine or the same machine on
       another port has none — and this model predates the export being kept on the
       server, so there is nothing to fetch. Said plainly, because the alternative
       is a named model over an empty viewport, which reads as a load that has not
       finished and never will. */
    /* Only for a model with nothing stored to render. One whose geometry is in the
       bucket is drawn from that file on every device, so there is no "not on this
       device" to report — see `rendered` in hero() above. */
    if (!model.hasGeometry && meshAvailability(model, s.modelMeshState) === 'absent') {
      return h('span', {
        class: 'card__hint',
        style: 'text-transform:none;letter-spacing:0',
        /* TWO DIFFERENT DEAD ENDS, and telling them apart is the difference between
           a reload and a trip back to CSiBridge. With no stored export there is
           nothing to fetch and never will be; with one, the fetch or the replay
           failed and trying again is worth something. */
        text: model.hasSource
          ? `${model.name} could not be loaded onto this device — showing the saved ` +
            'still instead. Reload to try again, or import it again.'
          : `${model.name} was imported on another device, and its export was not ` +
            'kept — its geometry cannot be fetched here. Import it again.',
      });
    }

    /* The name is in the status bar. What stays here is the one caveat on it. */
    return s.modelsRemembered && s.modelsRead === 'unreachable'
      ? h('span', {
          class: 'card__hint',
          style: 'text-transform:none;letter-spacing:0;color:var(--txt3)',
          /* WHERE THE NAME CAME FROM, on the one reading where it is not the
             register. src/core/modelIndexCache.ts lets the record open on the index
             this device was last given, and then the read failed, so nobody has
             confirmed since that this model is still there. */
          text: 'Last known to this device, not confirmed just now',
        })
      : null;
  }

  if (s.backend !== 'live') {
    return h('span', {
      class: 'card__hint',
      style: 'text-transform:none;letter-spacing:0',
      text: `Saved models cannot be listed — ${s.backendNote || 'the backend is unreachable'}`,
    });
  }
  /* THE BACKEND IS THERE AND THIS READ STILL DID NOT ARRIVE, which is a third
     state and used to be drawn as the fourth. A timed-out index read reported
     "No 3D model built" — the register stating a fact about a structure on the
     strength of a request that failed, which is the thing the Works card was
     given `progressRead` to stop it doing. Measured on this deployment: the index
     read took six to twelve seconds against remote bindings and was cut off by
     the client's own timeout, on a bridge holding two models and their geometry. */
  if (s.modelsRead === 'unreachable' && modelled === 0) {
    return h('span', {
      class: 'card__hint',
      style: 'text-transform:none;letter-spacing:0',
      text: 'The saved models could not be listed — the register did not answer. This says ' +
        'nothing about what has been modelled here.',
    });
  }
  /* MODELS ON FILE AND NOTHING TO DRAW. This used to read "n models saved, none
     rendered yet", which was written when a still always followed a save within
     seconds — so "yet" was a promise the app could keep. It cannot keep it now:
     nothing renders a model except an import, so a model that is here with no
     still, no exported geometry and no imported mesh in its state is not waiting
     for anything, and saying "yet" told the engineer to keep waiting for something
     that was never going to arrive. What it actually means is an import that did
     not finish — the shell was created and the bridge never landed in it — and the
     two things that fix it are the two things named here. */
  if (modelled) {
    return h('span', {
      class: 'card__hint',
      style: 'text-transform:none;letter-spacing:0',
      text:
        `${modelled} model${modelled === 1 ? '' : 's'} on file with no geometry in ` +
        `${modelled === 1 ? 'it' : 'any of them'} — the import did not finish. ` +
        'Delete and import again.',
    });
  }
  return h('span', { class: 'card__hint', text: 'No 3D model built' });
}

/* --------------------------- General (inventory sheet) --------------------------- */

/**
 * The fields on this card whose source is not the drawing the rest came off.
 *
 * `GR-20`. Three values on every record are weaker than everything beside them
 * and were drawn identically: the position is a map pin, the deck axis is an
 * OpenStreetMap way. Both are good enough for what the map does with them and
 * neither is a surveyed figure, which is worth knowing before quoting one.
 */
const SOURCED_FIELDS = ['lat', 'deckBearing'] as const;

/** What used to be the General group's place facts, drawn under Location now. */
function generalFacts(b: BridgeView, s: Readonly<State>): HTMLElement[] {
  const at = (field: (typeof SOURCED_FIELDS)[number]) => provenanceOf(b, field, s.changes);

  return [
    fact('Road length', b.roadLength != null ? `${b.roadLength.toFixed(2)} km` : '—'),
    wide(fact('Division road office', b.divisionOffice ?? '—')),
    fact(
      'Position',
      b.lat != null && b.lng != null
        ? sourced(`${b.lat.toFixed(6)}, ${b.lng.toFixed(6)}`, at('lat'))
        : '—',
    ),
    fact(
      'Deck bearing',
      b.deckBearing != null ? sourced(`${b.deckBearing.toFixed(1)}°`, at('deckBearing')) : '—',
    ),
    fact('Over / under', b.overUnder ?? '—'),
    fact('Left bank', b.leftBankDistrict ? `${b.leftBankDistrict}${b.leftBankVdc ? `, ${b.leftBankVdc}` : ''}` : '—'),
    fact('Right bank', b.rightBankDistrict ? `${b.rightBankDistrict}${b.rightBankVdc ? `, ${b.rightBankVdc}` : ''}` : '—'),
  ];
}


/* --------------------------- consequence --------------------------- */

/**
 * What a closure here would cost.
 *
 * Beneath General rather than beside the screening panel on purpose. Exposure
 * and consequence are two axes and the record shows both, but putting them in
 * one box invites the reader to combine them into a single verdict — which is
 * exactly the blended number src/data/consequence.ts refuses to produce.
 *
 * Road classification leads because it is the one line that is always
 * answerable: it is read off the route code rather than stored, so it is
 * present on every record whether or not anybody has done a traffic count.
 */
function consequenceCard(b: BridgeView, s: Readonly<State>): HTMLElement {
  const facts = consequenceFacts(b);
  const recorded = facts.filter((f) => f.known).length;

  return section(
    s,
    'consequence',
    'Consequence of closure',
    recorded === facts.length ? undefined : `${recorded} of ${facts.length} recorded`,
    [
      ...facts.map((f) =>
        h(
          'div',
          /* The fact nobody has recorded is set apart: on most records it is
             the one line in this group worth acting on. */
          {
            class: [!f.known && 'fact--gap', !f.known && 'fact--wide'],
            ...(f.note ? { title: f.note } : {}),
          },
          fact(f.label, f.value),
        ),
      ),
    ],
    {
      grid: true,
      aside:
        recorded === facts.length
          ? null
          : h(
              'span',
              { class: 'grp__meter', 'aria-hidden': 'true' },
              ...facts.map((f) => h('span', { class: ['grp__seg', f.known && 'grp__seg--on'] })),
            ),
    },
  );
}

/* --------------------------- Construction details --------------------------- */

/**
 * A value the accepted handover wrote (LC-11), marked M where it is the
 * as-built model's and H where it was entered in its place — and the same for
 * a works completion (W, LC-82) and the form filled from a model (LC-83).
 * Every other value is drawn as it always was.
 */
function atHandover(b: BridgeView, s: Readonly<State>, field: keyof Bridge, value: string): Child {
  if (value === '—' || value === NOT_RECORDED) return value;
  const p = provenanceOf(b, field, s.changes);
  return p.tier === 'model' || p.tier === 'handover' || p.tier === 'works' ? sourced(value, p) : value;
}

function constructionCard(b: BridgeView, s: Readonly<State>): HTMLElement {
  const girders = b.handover?.girders ?? null;
  return section(s, 'construction', 'Construction details', undefined, [
    fact('Span length', b.spanLength != null ? atHandover(b, s, 'spanLength', `${b.spanLength.toFixed(2)} m`) : '—'),
    /* The girder count has no inventory column; the handover keeps it. */
    ...(girders !== null
      ? [fact('Girders', b.handover!.modelFields.includes('girders') ? sourced(String(girders), modelProvenance(b.handover!.modelId, b.handover!.completedOn)) : String(girders))]
      : []),
    fact('Pier type', b.pierType ?? '—'),
    wide(fact('Abutment type', b.abutmentType ?? '—')),
    wide(fact('Abutment foundation', atHandover(b, s, 'abutmentFoundation', b.abutmentFoundation ?? '—'))),
    wide(fact('Pier foundation', atHandover(b, s, 'pierFoundation', b.pierFoundation ?? '—'))),
    wide(fact('Bearings', atHandover(b, s, 'bearings', b.bearings ?? '—'))),
    fact('Railings', b.railings ?? '—'),
    fact('Movement', b.movement ?? '—'),
    wide(fact('River training', b.riverTraining ?? '—')),
    fact('Running surface', b.runningSurface ?? '—'),
    fact('Road signs', b.roadSigns ?? '—'),
    wide(fact('Services', b.services ?? '—')),
  ], { grid: true });
}

/**
 * A chip per cycle the structure has: the baseline while it is due (LC-60),
 * routine and principal always, and underwater where an interval is set.
 */
function cycleChips(standing: InspectionStanding): HTMLElement[] {
  return [standing.baseline, standing.special, standing.routine, standing.principal, standing.underwater]
    .filter((c) => c !== null)
    .map((c) => tag(cycleChipLabel(c), c.rank, cycleLabel(c)));
}

/* --------------------------- State --------------------------- */

/**
 * Where the structure is in its life (LC-10): the state, since when and on
 * whose decision, and the model of record (LC-12). Its history is Bridge
 * info's, under History.
 * What changes it is in the Edit state dialog (src/screens/stateDialog.ts),
 * opened from the panel header — or from here in the field shell, which has
 * no panel header.
 */
/**
 * A row Edit state can fill, kept on show while empty: every field in the
 * dialog is a row here, so the dialog never asks for something the panel hides.
 */
function editable(row: HTMLElement): HTMLElement {
  row.removeAttribute('data-empty');
  return row;
}

function stateBlock(b: BridgeView, s: Readonly<State>): HTMLElement {
  const p = phaseOf(b);
  const mor = b.modelOfRecord ?? null;
  const morName = mor ? (modelsOf(b, s).find((m) => m.id === mor.modelId)?.name ?? mor.modelId) : null;
  const block = section(s, 'state', 'State', undefined, [
    fact('State', h('span', { class: 'reg__state' }, phaseChip(b), worksChip(b))),
    fact('Since', p.recorded ? sinceLine({ ...b, phaseBy: null }, prettyDate) : '—'),
    /* A bridge recorded in service (LC-80), and works open on it (LC-81). */
    ...(b.inServiceOn
      ? [fact('In service', /^\d{4}$/.test(b.inServiceOn) ? b.inServiceOn : prettyDate(b.inServiceOn))]
      : mayRecordInService(b, s)
        ? [editable(fact('In service', '—'))]
        : []),
    ...(b.works ? [fact('Works', join([WORKS_KIND_LABEL[b.works.kind], b.works.since ? `since ${prettyDate(b.works.since.slice(0, 10))}` : null], ' · '))] : []),
    ...(b.worksCompleted
      ? [fact('Works completed', join([prettyDate(b.worksCompleted.completedOn), b.worksCompleted.acceptedBy], ' · '))]
      : []),
    fact('Model of record', mor ? `${morName} · ${mor.kind}` : '—'),
    /* The accepted handover (LC-11): when the cycle started, and the liability period. */
    ...(b.handover
      ? [
          fact('Handed over', join([prettyDate(b.handover.completedOn), b.handover.acceptedBy], ' · ')),
          fact('Defects liability', b.handover.dlpEndsOn ? `Until ${prettyDate(b.handover.dlpEndsOn)}` : '—'),
        ]
      : []),
    /* Open, posted at N t, or closed, and on what basis (LC-23). */
    fact('Operational', statusChip(b)),
    fact('Decided', decidedLine({ ...operationalStatus(b), by: null, at: null }) || '—'),
    /* When it is due to be rated again, and why early (LC-24). */
    fact('Re-rating', (() => {
      const due = ratingDueFor(b, s.registerDefects);
      return due === null ? '—' : (reratingChip(due) ?? 'Never rated');
    })()),
  ]);
  if (s.collapsedGroups.includes('state')) return block;
  block.append(
    h(
      'div',
      { class: 'det-state' },
      s.shell === 'field' && stateEdits(b, s).length
        ? button('Edit state', { sm: true, iconPath: ICONS.edit, onClick: () => actions.openStateEdit() })
        : null,
      /* The model of record's refusal lands here; the dialog shows its own. */
      s.phaseNote && !s.stateEditOpen ? h('div', { class: 'dlg__note dlg__note--error', text: s.phaseNote }) : null,
    ),
  );
  return block;
}

/* --------------------------- History --------------------------- */

/**
 * Who built it and when, then every move of its state (LC-10), oldest first —
 * read with the structure, see `loadBridgeContext`.
 */
function historyCard(b: BridgeView, s: Readonly<State>): HTMLElement {
  return section(s, 'history', 'History', undefined, [
    fact('Designed by', b.designedBy ?? '—'),
    fact('Constructed by', b.constructedBy ?? '—'),
    fact('Year of completion', count(b.year)),
    ...(s.phaseHistory === null && s.backend === 'live'
      ? [fact('State history', 'Reading…')]
      : (s.phaseHistory ?? []).map((t) =>
          fact(
            prettyDate(t.effectiveOn ?? t.at.slice(0, 10)),
            join(
              [
                ACT_LABEL[actOfRow(t)],
                t.effectiveOn && t.effectiveOn !== t.at.slice(0, 10) ? `recorded ${prettyDate(t.at.slice(0, 10))}` : null,
                t.by,
                t.reason,
              ],
              ' · ',
            ),
          ),
        )),
  ]);
}

/** How each state reads, and how loudly. Accepted is the quiet one — it is the norm. */
const STATE_RANK: Record<InspectionState, 1 | 2 | 3 | 4> = {
  draft: 3,
  review: 2,
  accepted: 1,
};

const STATE_LABEL: Record<InspectionState, string> = {
  draft: 'Draft',
  review: 'In review',
  accepted: 'Accepted',
};

/**
 * Where an inspection has got to, and the control that moves it.
 *
 * An entry with no `state` predates the workflow — a paper record transcribed
 * into the register long before there was anything to review. It reads as "On
 * file" and offers no control: there is nothing to accept, nobody to attribute
 * an acceptance to, and inventing either would be worse than saying so.
 */
/**
 * Where one inspection has got to, and the control that moves it.
 *
 * `mayAccept` is `inspection.accept`, which an inspector does not hold: accepting
 * your own inspection is not a review of it. Without it the cell is the chip
 * alone — which is what the row already said before the picker existed, and is
 * still true.
 */
function inspectionStateCell(
  insp: BridgeView['inspections'][number],
  mayAccept: boolean,
  refused: State['refused'] = null,
): HTMLElement {
  /* Recorded here and not yet in the register. Shown so an inspector can see
     that what they entered at the structure took, and shown as PENDING so they
     do not believe it has been filed. There is nothing to review yet and no id
     to address it by — both arrive with the row the server writes. */
  if (insp.pending) {
    return h(
      'td',
      {},
      tag('On this device', 3, 'Recorded here and waiting to sync. It is not in the register yet.'),
    );
  }

  if (!insp.state || !insp.id) {
    return h(
      'td',
      {},
      h('span', {
        style: 'color:var(--txt3);font-size:11.5px',
        title:
          'Recorded before the review workflow existed. On file as a fact; it never went through it.',
        text: 'On file',
      }),
    );
  }

  const state = insp.state;
  const id = insp.id;

  return h(
    'td',
    {},
    tag(
      STATE_LABEL[state],
      STATE_RANK[state],
      state === 'accepted' && insp.reviewer
        ? `Accepted by ${insp.reviewer}`
        : state === 'accepted'
          ? 'Accepted while Access was not enforced — not attributable'
          : 'Not yet accepted',
    ),
    mayAccept
      ? h(
          'select',
          {
            class: 'select select--sm',
            style: 'margin-top:4px',
            'aria-label': `State of the inspection of ${prettyDate(insp.date)}`,
            onchange: revertOnRefusal(state, (v) => actions.setInspectionState(id, v as InspectionState)),
          },
          ...(['draft', 'review', 'accepted'] as InspectionState[]).map((st) =>
            h('option', { value: st, selected: st === state, text: STATE_LABEL[st] }),
          ),
        )
      : null,
    mayAccept ? refusalNote(refused, id) : null,
  );
}

/* --------------------------- what changed --------------------------- */

/** A stored value as the log holds it — null is a genuinely empty field. */
function changeValue(v: string | null): string {
  return v === null || v === '' ? '—' : v;
}

const SOURCE_LABEL: Record<string, string> = {
  edit: 'Inventory edit',
  placement: 'Placed on the map',
  inspection: 'Inspection state',
};

/**
 * The append-only history of this record.
 *
 * Closed by default and read when it is opened: most visits to a record are not
 * about who last touched it, and this is the one per-structure collection that
 * grows without bound.
 *
 * A hand-placed position shows up here as "Placed on the map" with the
 * coordinates it moved between, which is the specific gap the finding named —
 * dragging a structure used to move three fields and record nothing about who
 * did it or why.
 */
function historyBlock(_b: BridgeView, s: Readonly<State>): HTMLElement {
  const rows = s.changes ?? [];

  const blockIsOpen = blockOpen(s, 'changes');
  const head = foldHead(blockIsOpen, foldToggle(s, 'changes'),
      'What changed',
      s.changesOpen && s.changes ? `${rows.length} recorded` : undefined,
      button(s.changesOpen ? 'Hide' : 'Show history', {
        sm: true,
        onClick: () => void actions.toggleChanges(),
      }),
    );
  if (!blockIsOpen) return shutBlock(head);

  return h(
    'div',
    { class: 'det-block det-block--flush' },
    head,
    !s.changesOpen
      ? null
      : s.changes === null
        ? h('div', { class: 'note', text: 'Reading…' })
        : rows.length
          ? h(
              'table',
              { class: 'tbl' },
              h(
                'thead',
                {},
                h(
                  'tr',
                  {},
                  h('th', { style: 'width:104px', text: 'When' }),
                  h('th', { style: 'width:138px', text: 'What' }),
                  h('th', { text: 'Field' }),
                  h('th', { text: 'From' }),
                  h('th', { text: 'To' }),
                  h('th', { style: 'width:170px', text: 'By' }),
                ),
              ),
              h(
                'tbody',
                {},
                ...rows.map((c) =>
                  h(
                    'tr',
                    {},
                    h('td', {
                      class: 'num nowrap',
                      style: 'color:var(--txt2)',
                      text: prettyDate(c.changedAt.slice(0, 10)),
                    }),
                    h('td', { style: 'color:var(--txt2)', text: SOURCE_LABEL[c.source] ?? c.source }),
                    h('td', { class: 'nowrap', text: c.field }),
                    h('td', { style: 'color:var(--txt3)', text: changeValue(c.oldValue) }),
                    h('td', { style: 'font-weight:500', text: changeValue(c.newValue) }),
                    h('td', {
                      class: 'ellipsis',
                      style: 'color:var(--txt2);font-size:11.5px',
                      // Null is "we could not tell", which is a different claim
                      // from a name and has to read as one.
                      text: c.author ?? 'not attributable',
                    }),
                  ),
                ),
              ),
            )
          : h('div', {
              class: 'reg__empty',
              text:
                s.backend === 'live'
                  ? 'No changes recorded.'
                  : 'The register could not be reached.',
            }),
  );
}

/* ---------------------------------------------------------------------------
   Defects — one card, one store, two ways in.

   There used to be two cards here. "Defects" listed rows from the register's own
   table, flat; "Components" listed the modeller's separate log, hung off the 3D
   model's component tree. They were two records of what is often the same
   finding: the same crack, logged by an inspector who picked a pier in the 3D
   view or by an engineer who typed it into a dialog, counted twice on one screen
   and once on the overview. Migration 0015 made the table the single store for
   both, and this is the single card over it.

   BOTH DOORS ARE OFFERED, deliberately, because they suit different moments.
   "Log defect" is a dialog and needs no geometry on screen — it is what you use
   at a desk, or on a phone with a photograph in your hand. "Defects inspector"
   opens the model and lets you pin the finding to the exact point on the exact
   girder. Either writes the same row, so which one somebody prefers is a matter
   of taste and not of what the register will remember.

   THE TREE IS SCAFFOLDING, not a source. The inspectable-component tree (Spans >
   Span-1 > Deck-1, Bents > Bent-1 > Bent Cap) comes from the hero viewport's own
   loaded model, reported live over postMessage — see src/modeler/viewport.ts and
   ComponentNode in src/data/types.ts. It exists only in that frame's memory and
   there is nothing to read out of D1 or R2 for it. Every component is listed,
   the sound ones too, because the table is also where an inspection is entered:
   a component's row opens the Log defect dialog on it. The model's single root
   ("Imported Model") is not drawn — it is the whole structure, and a row for it
   only indents everything under it.

   And it can be absent — the viewport is still loading, or this structure's
   model has never been rendered. The rows are still shown when it is, flat,
   because a defect is a record in its own right and the card must not go blank
   waiting for geometry to arrive.
   --------------------------------------------------------------------------- */

interface DefectRow {
  /** Label path from the tree root, e.g. "Spans>Span-1>Deck-1" — the fold key. */
  path: string;
  label: string;
  depth: number;
  isComponent: boolean;
  hasChildren: boolean;
  isOpen: boolean;
  /** Whether this is the node's own line — false for its 2nd+ defect-duplicate row (see below). */
  isFirst: boolean;
  /** null on a group row, which never carries one. */
  defect: RegisterDefect | null;
  /** What a defect logged from this row stores — its qualified name; '' for a group. */
  value: string;
  /** The model's id for the component, where it has one. */
  id: string | null;
}

/* ---------------------------------------------------------------------------
   NAMING. `RegisterDefect.component` is a name, not an id, and the same
   component is written two different ways depending on where the defect was
   logged — the tree node's own label from a list, the concatenated mesh name
   from a 3D pick. `indexComponentNames` in src/data/componentTree.ts is that
   rule and the note explaining it; this file is one of its two callers, the
   other being the picker in the "Log defect" dialog.

   What is decided HERE is what to do about ambiguity: a defect stored with the
   model's id for its component goes on the node carrying that id; otherwise it
   is placed only where exactly one node answers to what it says. "Bent Cap" on a five-bent
   viaduct names five nodes, and repeating one defect under each would report
   five where the inspector logged one — so it goes under "Not on this model"
   instead, which is visible and honest rather than quietly wrong.
   --------------------------------------------------------------------------- */

/**
 * Depth-first walk producing a row for every node, and one per defect on a
 * node that has them. A component with more than one defect is repeated, so
 * every defect gets its own Type/Severity/Status/Note/Logged rather than being
 * collapsed into a count.
 *
 * Keyed by tree path rather than by label, because the label is not unique —
 * see the naming note above.
 *
 * Every node is visited even when its parent is folded away, because a defect
 * filed by id is claimed by the first node carrying it in walk order, folded or
 * not. Only the rows are dropped, not the walk.
 */
function flattenComponentTree(
  nodes: readonly ComponentNode[],
  depth: number,
  parentPath: string,
  collapsed: ReadonlySet<string>,
  defectsByPath: ReadonlyMap<string, RegisterDefect[]>,
  defectsById: ReadonlyMap<string, RegisterDefect[]>,
  /** Ids already drawn: a part drawn as several meshes carries its id on each. */
  drawn: Set<string>,
  /** The qualified name so far — see src/data/componentTree.ts. */
  prefix = '',
  /** A node walked for its name and children but given no row of its own. */
  skip: ComponentNode | null = null,
): DefectRow[] {
  const out: DefectRow[] = [];
  for (const node of nodes) {
    const path = `${parentPath}>${node.label}`;
    const bare = bareLabel(node.label);
    const qualified = node.isComponent ? (prefix ? `${prefix} ${bare}` : bare) : prefix;
    const own = node !== skip;
    const isOpen = !own || !collapsed.has(path);
    const onId = node.id && !drawn.has(node.id) ? (defectsById.get(node.id) ?? []) : [];
    if (onId.length) drawn.add(node.id!);
    const matches = [...(defectsByPath.get(path) ?? []), ...onId];
    const childRows = flattenComponentTree(
      node.children,
      own ? depth + 1 : depth,
      path,
      collapsed,
      defectsByPath,
      defectsById,
      drawn,
      qualified,
    );
    const hasChildren = childRows.length > 0;
    const row = {
      path,
      label: node.label,
      depth,
      isComponent: node.isComponent,
      hasChildren,
      isOpen,
      value: node.isComponent ? qualified : '',
      id: node.isComponent ? (node.id ?? null) : null,
    };

    if (matches.length === 0) {
      if (own) out.push({ ...row, isFirst: true, defect: null });
    } else {
      matches.forEach((defect, i) => {
        out.push({ ...row, isFirst: i === 0, defect });
      });
    }

    if (isOpen) out.push(...childRows);
  }
  return out;
}

/**
 * Sort each defect onto the node carrying its component id, else onto the one
 * node that answers to its component name, or into `unplaced` if none does or
 * several do.
 *
 * A defect can outlive the component it names — renamed, deleted, or logged
 * against a different model of the same structure — and it can name something
 * ambiguous. It can also name nothing at all: a defect somebody saw is worth
 * recording even when they could not say precisely where, and the rows written
 * before the dialog stopped asking carry a coarse `element` instead. Every one
 * of them is still shown; dropping one silently would be the worst outcome,
 * since the register would then report fewer defects than were logged and give
 * no sign that it had.
 */
function placeDefects(
  defects: readonly RegisterDefect[],
  names: ReadonlyMap<string, string[]>,
  ids: ReadonlySet<string>,
): { byPath: Map<string, RegisterDefect[]>; byId: Map<string, RegisterDefect[]>; unplaced: RegisterDefect[] } {
  const byPath = new Map<string, RegisterDefect[]>();
  const byId = new Map<string, RegisterDefect[]>();
  const unplaced: RegisterDefect[] = [];
  const file = (into: Map<string, RegisterDefect[]>, key: string, defect: RegisterDefect): void => {
    const at = into.get(key);
    if (at) at.push(defect);
    else into.set(key, [defect]);
  };

  for (const defect of defects) {
    /* The id first: it names one part where the name may name several. */
    if (defect.componentId && ids.has(defect.componentId)) {
      file(byId, defect.componentId, defect);
      continue;
    }
    const claims = defect.component === null ? [] : (names.get(defect.component) ?? []);
    if (claims.length !== 1) {
      unplaced.push(defect);
      continue;
    }
    file(byPath, claims[0]!, defect);
  }

  return { byPath, byId, unplaced };
}

/** What to call a defect that did not place, in the Component column. */
function unplacedLabel(d: RegisterDefect): string {
  if (d.component) return d.component;
  if (d.element) return elementLabel(d.element);
  return 'Location not recorded';
}

/** Defects that could not be placed, as flat rows under their own group. */
function unplacedRows(defects: readonly RegisterDefect[], depth: number): DefectRow[] {
  return defects.map((defect) => ({
    path: `!unplaced>${defect.id}`,
    label: unplacedLabel(defect),
    depth,
    isComponent: true,
    hasChildren: false,
    isOpen: true,
    isFirst: true,
    defect,
    value: '',
    id: null,
  }));
}

/**
 * The card.
 *
 * Always rendered, and never null. The old Components card returned null when no
 * model had a captured still, on the reasoning that without a viewport there was
 * no tree to draw — which was right for a card that was only ever a view onto a
 * model. This one holds the register's record of what is wrong with the
 * structure, and that is worth showing whether or not the geometry has loaded.
 */
function defectsBlock(b: BridgeView, s: Readonly<State>): HTMLElement {
  const mine = s.registerDefects.filter((d) => d.bridgeId === b.id);
  const open = mine.filter((d) => isOpenStatus(d.status));
  const roots = s.heroComponentTree;
  const collapsed = new Set(s.componentTreeCollapsed);

  /* With no tree yet, every defect is "unplaced" — which is the honest shape
     rather than a defect: they are all shown, flat, at the top level, and they
     re-hang themselves under their components when the viewport reports one. */
  const { byPath, byId, unplaced } = roots
    ? placeDefects(mine, indexComponentNames(roots), indexComponentIds(roots))
    : { byPath: new Map<string, RegisterDefect[]>(), byId: new Map<string, RegisterDefect[]>(), unplaced: mine };

  /* A lone root is the model itself, so its children are drawn as the top level. */
  const skip = roots?.length === 1 && roots[0]!.children.length ? roots[0]! : null;
  /* Walked fully open and folded afterwards (see filterRows), so a filter can
     find a match under a folded group and keep the group that leads to it. */
  const all = roots ? flattenComponentTree(roots, 0, '', new Set(), byPath, byId, new Set(), '', skip) : [];
  const mayLog = can(s, 'defect.log');

  if (unplaced.length) {
    /* Under a heading only when there is a tree for them to be outside of. With
       no tree there is no "elsewhere" to be, and a group row would imply the
       register had looked and not found them. */
    if (all.length || roots) {
      all.push({
        path: '!unplaced',
        label: 'Not on this model',
        depth: 0,
        isComponent: false,
        hasChildren: true,
        isOpen: true,
        isFirst: true,
        defect: null,
        value: '',
        id: null,
      });
      all.push(...unplacedRows(unplaced, 1));
    } else {
      all.push(...unplacedRows(unplaced, 0));
    }
  }

  const query = s.inspectionFilter.trim();
  const filtering = query !== '' || s.inspectedOnly;
  const rows = filterRows(all, query, s.inspectedOnly, collapsed);

  const blockIsOpen = blockOpen(s, 'defects');
  const head = foldHead(blockIsOpen, foldToggle(s, 'defects'),
      'Inspection',
      mine.length ? `${open.length} open of ${mine.length}` : undefined,
      ...(blockIsOpen && all.length ? [inspectionFilterBar(s)] : []),
    );
  if (!blockIsOpen) return shutBlock(head);

  return h(
    'div',
    { class: 'det-block det-block--flush' },
    head,
    rows.length
      ? h(
          'table',
          { class: 'tbl', style: 'margin:0 0 10px' },
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { text: 'Component' }),
              h('th', { style: 'width:56px', text: 'Code' }),
              h('th', { text: 'Type' }),
              h('th', { style: 'width:92px', text: 'Severity' }),
              h('th', { style: 'width:112px', text: 'Status' }),
              h('th', { text: 'Note' }),
              h('th', { style: 'width:96px', text: 'Seen' }),
            ),
          ),
          h(
            'tbody',
            {},
            ...rows.map((row) =>
              defectRow(row, can(s, 'defect.progress'), s.refused, mayLog
                ? (r) => actions.openLogDefect(b.id, { component: r.value, componentId: r.id ?? '' })
                : null),
            ),
          ),
        )
      : h('div', {
          class: 'reg__empty',
          /* Two different nothings, and which one it is matters: the register
             being unreachable is not a claim that there are no defects. Anything
             logged but unplaceable is caught by placeDefects above, so a defect
             can never be the reason this branch is taken. */
          text:
            filtering && all.length
              ? query
                ? `Nothing${s.inspectedOnly ? ' inspected' : ''} matches “${query}”.`
                :'No component on this structure has been inspected.'
              : s.backend !== 'live'
                ? 'The register could not be reached.'
                : 'No inspection logged against this structure.',
        }),
  );
}

/** The Inspection tab's find box and its All / Inspected switch, in the table's head. */
function inspectionFilterBar(s: Readonly<State>): HTMLElement {
  return h(
    'div',
    { class: 'insp-filter' },
    h(
      'label',
      { class: 'insp-filter__find' },
      icon(ICONS.search, { size: 13, width: 1.6, class: 'insp-filter__icon' }),
      h('input', {
        // The id is what keeps focus across the repaint each keystroke causes.
        id: 'insp-filter',
        class: 'input insp-filter__input',
        type: 'search',
        placeholder: 'Filter component, type, status, note…',
        'aria-label': 'Filter the inspection table',
        autocomplete: 'off',
        value: s.inspectionFilter,
        oninput: (e: Event) => actions.setInspectionFilter((e.target as HTMLInputElement).value),
      }),
    ),
    seg([
      { label: 'All', on: !s.inspectedOnly, title: 'Every component of the model', onClick: () => actions.setInspectedOnly(false) },
      { label: 'Inspected', on: s.inspectedOnly, title: 'Only components with something logged on them', onClick: () => actions.setInspectedOnly(true) },
    ]),
  );
}

/** The paths of every group a row sits under, nearest last. */
function ancestorPaths(path: string): string[] {
  const out: string[] = [];
  for (let i = 1; i < path.length; i++) if (path[i] === '>') out.push(path.slice(0, i));
  return out;
}

/**
 * The rows to draw: those matching the find box and the Inspected switch, with
 * every group leading to one kept, then folded as the reader left them.
 *
 * A group whose own name matches keeps everything under it, so "Span 1" lists
 * the span rather than only its heading. A row's text is its component and, if
 * it carries one, the record's code, type, severity, status, note and author.
 */
function filterRows(
  all: readonly DefectRow[],
  query: string,
  inspectedOnly: boolean,
  collapsed: ReadonlySet<string>,
): DefectRow[] {
  const q = query.toLowerCase();
  const labels = new Map<string, string>();
  for (const r of all) if (r.isFirst) labels.set(r.path, r.label.toLowerCase());
  const hit = (r: DefectRow): boolean => {
    if (inspectedOnly && r.defect === null) return false;
    if (!q) return true;
    if (r.label.toLowerCase().includes(q)) return true;
    if (ancestorPaths(r.path).some((p) => labels.get(p)?.includes(q))) return true;
    const d = r.defect;
    return d !== null && [d.code, defectCodeLabel(d.code), d.severity, d.status, d.note, d.author ?? '']
      .some((t) => t.toLowerCase().includes(q));
  };

  const shown = new Set<DefectRow>();
  const leading = new Set<string>();
  for (const r of all) {
    if (!hit(r)) continue;
    shown.add(r);
    for (const p of ancestorPaths(r.path)) leading.add(p);
  }
  const kept = all.filter((r) => shown.has(r) || (r.isFirst && leading.has(r.path)));

  /* Whether a group still has anything under it is a question about what was
     kept, not about the model — a chevron over nothing would fold nothing. */
  const parents = new Set<string>();
  for (const r of kept) for (const p of ancestorPaths(r.path)) parents.add(p);
  return kept
    .filter((r) => !ancestorPaths(r.path).some((p) => collapsed.has(p)))
    .map((r) => ({ ...r, hasChildren: parents.has(r.path), isOpen: !collapsed.has(r.path) }));
}

/**
 * One defect in full, as a dialog over the Defects page: what it is, how bad,
 * where it has got to, the work recommended for it, who saw it and when, and its
 * photograph. Opened from its pin on the 3D model or its row in the list.
 */
function defectDetail(b: BridgeView, d: RegisterDefect, s: Readonly<State>): HTMLElement {
  const where = d.component ?? unplacedLabel(d);
  const type = defectCodeLabel(d.code);
  const close = () => actions.showDefect(null);
  return h(
    'div',
    { class: 'dlg', ...scrim(close) },
    h(
      'div',
      { class: 'dlg__panel dfd', role: 'dialog', 'aria-modal': 'true', 'aria-label': `${type} on ${where}` },
      h(
        'div',
        { class: 'dfd__head' },
        h('div', { class: 'dfd__code', text: d.code }),
        h(
          'div',
          { class: 'dfd__heading' },
          h('h2', { class: 'dfd__title' }, type, h('span', { class: 'dfd__on', text: ' on ' }), where),
          h(
            'div',
            { class: 'dfd__meta' },
            tag(d.severity, severityRank(d.severity)),
            h('span', { class: 'dfd__status', 'data-status': d.status, text: d.status }),
            h('span', { text: `Seen ${d.loggedAt ? prettyDate(d.loggedAt) : '—'}` }),
            h('span', { class: 'dfd__dot', text: '·' }),
            h('span', { text: d.pin ? 'On the 3D model' : 'Component only, no point' }),
          ),
        ),
        h('button', { type: 'button', class: 'dlg__close', title: 'Close', 'aria-label': 'Close', onclick: close }, '×'),
      ),
      defectFlow(b, d, s),
      h(
        'div',
        { class: 'dlg__body dfd__body' },
        h(
          'div',
          { class: 'dfd__top' },
          d.hasPhoto
            ? h('div', { class: 'dfd__photos' }, ...defectPhotos(d))
            : h('div', { class: 'dfd__nophoto', text: 'No photograph is attached to this issue.' }),
          h(
            'div',
            { class: 'dfd__finding' },
            h('div', { class: 'dfd__cap', text: 'Finding' }),
            h(
              'div',
              { class: 'dfd__facts' },
              fact('Component', where),
              fact('Found on', foundOn(b, d)),
              fact('Logged by', d.author ?? 'not attributable'),
              fact('Size', d.quantity ? describeQuantity(d.code, d.quantity) : '—'),
              d.repair ? fact('Repaired', `${prettyDate(d.repair.on)} · ${d.repair.ref} · ${d.repair.by ?? 'not attributable'}`) : null,
            ),
            d.note ? h('p', { class: 'dfd__note', text: d.note }) : null,
          ),
        ),
        defectRemedial(b, d, s),
        h('div', { class: 'dfd__history' }, defectVisits(b, d, s), defectStatusHistory(s, d)),
      ),
    ),
  );
}

/** The path a defect takes, the step it is on, and the moves on offer from there. */
function defectFlow(b: BridgeView, d: RegisterDefect, s: Readonly<State>): HTMLElement {
  const steps: readonly string[] = d.status === 'Resolved' ? ['Open', 'In Review', 'Resolved'] : ['Open', 'In Review', 'Repaired', 'Verified'];
  const at = steps.indexOf(d.status);
  return h(
    'div',
    { class: 'dfd__flow' },
    h(
      'div',
      { class: 'dfd__strip' },
      h(
        'ol',
        { class: 'dfd__steps', 'aria-label': 'Where the issue has got to' },
        ...steps.map((st, i) =>
          h('li', {
            class: ['dfd__step', i < at && 'dfd__step--done', i === at && 'dfd__step--on'],
            'aria-current': i === at ? 'step' : undefined,
            text: st,
          }),
        ),
      ),
      defectMoveBar(d, s),
    ),
    defectMoveForms(b, d, s),
  );
}

/**
 * The work recommended for this one defect, and what it would cost where the
 * register can say. Open defects only, as on the remedial table: a closed
 * defect's treatment is work already done.
 */
function defectRemedial(b: BridgeView, d: RegisterDefect, s: Readonly<State>): HTMLElement | null {
  if (!isOpenStatus(d.status)) return null;
  const est = estimateFor(b, [d], s.rateTables, todayIso(), undefined, wordingMap(s.treatmentWording));
  if (!est.lines.length) return null;
  return h(
    'div',
    { class: 'dfd__remedial' },
    ...est.lines.map((l) =>
      h(
        'div',
        { class: 'dfd__work' },
        h(
          'div',
          { class: 'dfd__work-head' },
          h(
            'div',
            { class: 'dfd__work-text' },
            h('div', { class: 'dfd__cap', text: 'Recommended work' }),
            h('div', { class: 'dfd__work-label', text: l.treatment.label }),
            h('div', { class: 'dfd__work-note', text: l.treatment.note }),
          ),
          h('span', {
            class: ['dfd__class', !isCostable(l.klass) && 'dfd__class--design'],
            title: CLASS_DIAGNOSTIC[l.klass],
            text: l.klass,
          }),
        ),
        l.amount === null
          ? h(
              'div',
              { class: 'dfd__refusal rem__refusal', role: 'note' },
              icon(ICONS.alert, { size: 14 }),
              h('span', {}, h('strong', { text: 'Not priced here. ' }), refusalText(l.refusal!)),
            )
          : h(
              'div',
              { class: 'dfd__cost' },
              fact('Quantity', l.quantity === null ? '—' : `${trimNumber(l.quantity)} ${l.unit}${l.method ? ` · ${methodLabel(l.method)}` : ''}`),
              fact('Rate', l.rate === null ? '—' : `${npr(l.rate)}${l.normRef ? ` · ${l.normRef}` : ''}`),
              h('div', { class: 'fact dfd__amount' }, h('span', { class: 'fact__k', text: 'Approximate amount' }), h('span', { class: 'fact__v', text: npr(l.amount) })),
            ),
      ),
    ),
  );
}

/** The inspection that found a defect, by its type and day, or what the record says instead. */
function foundOn(b: BridgeView, d: RegisterDefect): string {
  if (d.origin) return `Carried over at handover (${d.origin.ref})`;
  const i = d.inspectionId ? b.inspections.find((x) => x.id === d.inspectionId) : undefined;
  return i ? `${inspectionKindLabel(i)} · ${prettyDate(i.date)}` : 'No inspection named';
}

const MOVE_LABEL: Readonly<Record<string, string>> = {
  'In Review': 'Send to review',
  Resolved: 'Resolve',
  Repaired: 'Record repair…',
  Verified: 'Verify repair…',
  reopen: 'Reopen…',
};

/**
 * The moves this person may make from where the defect stands (LC-62):
 * forward, each by its capability, and a reopen. Verifying is not offered to
 * whoever recorded the repair; the server refuses it to them too.
 */
export function defectMoves(s: Readonly<State>, d: RegisterDefect): { to: string; label: string; form: boolean }[] {
  const progress = can(s, 'defect.progress');
  const verify = can(s, 'defect.verify');
  const me = s.identity?.author ?? null;
  const out: { to: string; label: string; form: boolean }[] = [];
  for (const st of DEFECT_STATUSES) {
    if (defectMove(d.status, st) !== 'forward') continue;
    if (st === 'Verified' ? !verify : !progress) continue;
    if (st === 'Verified' && me && d.repair?.by && normaliseIdentity(me) === normaliseIdentity(d.repair.by)) continue;
    out.push({ to: st, label: MOVE_LABEL[st]!, form: st === 'Repaired' || st === 'Verified' });
  }
  if (d.status !== 'Open' && (progress || verify)) out.push({ to: 'reopen', label: MOVE_LABEL['reopen']!, form: true });
  return out;
}

/**
 * The status moves on offer. The first forward move is the one filled: it is
 * where the defect goes next if nothing is wrong with it.
 */
function defectMoveBar(d: RegisterDefect, s: Readonly<State>): HTMLElement | null {
  const moves = defectMoves(s, d);
  const canSee = can(s, 'defect.log');
  if (!moves.length && !canSee) return null;
  const next = moves.find((mv) => mv.to !== 'reopen');
  return h(
    'div',
    { class: 'dfd__actions' },
    canSee ? button('Seen again…', { sm: true, onClick: () => actions.openDefectSight(d.id) }) : null,
    ...moves
      .slice()
      .reverse()
      .map((mv) =>
        button(mv.label, {
          sm: true,
          primary: mv === next,
          onClick: () => (mv.form ? actions.openDefectMove(d.id, mv.to) : void actions.setDefectStatus(d.id, mv.to)),
        }),
      ),
  );
}

/** The form for the move or sighting being written, and what the register said to the last one. */
function defectMoveForms(b: BridgeView, d: RegisterDefect, s: Readonly<State>): HTMLElement | null {
  const m = s.defectMove?.id === d.id ? s.defectMove : null;
  const sight = s.defectSight?.id === d.id ? s.defectSight : null;
  const refused = refusalNote(s.refused, d.id);
  if (!m && !sight && !refused) return null;
  return h(
    'div',
    { class: 'dfd__moves' },
    m ? defectMoveForm(m) : null,
    sight ? defectSightForm(b, d, sight) : null,
    s.defectMoveError && (m || sight) ? h('p', { class: 'dlg__error', role: 'alert', text: s.defectMoveError }) : null,
    refused,
  );
}

function defectMoveForm(m: NonNullable<State['defectMove']>): HTMLElement {
  const input = (id: string, label: string, value: string, key: 'note' | 'repairedOn' | 'repairRef', type = 'text') =>
    h(
      'label',
      { class: 'field', style: 'display:block;margin-top:8px' },
      h('span', { class: 'field__label', text: label }),
      h('input', {
        id,
        class: 'input',
        type,
        value,
        oninput: (e: Event) => actions.setDefectMoveField(key, (e.target as HTMLInputElement).value),
      }),
    );
  return h(
    'div',
    { class: 'dfd__form', 'data-move': m.to },
    m.to === 'Repaired' ? input('dfm-date', 'Repaired on', m.repairedOn, 'repairedOn', 'date') : null,
    m.to === 'Repaired' ? input('dfm-ref', 'Repair reference', m.repairRef, 'repairRef') : null,
    input('dfm-note', m.to === 'reopen' ? 'Why it is reopened' : 'Note', m.note, 'note'),
    h(
      'div',
      { style: 'display:flex;gap:6px;margin-top:8px' },
      button(m.to === 'reopen' ? 'Reopen' : m.to === 'Verified' ? 'Verify' : 'Record repair', {
        sm: true,
        primary: true,
        onClick: () => void actions.submitDefectMove(),
      }),
      button('Cancel', { sm: true, onClick: () => actions.cancelDefectMove() }),
    ),
  );
}

/** A later inspection seeing the defect again: which, how bad then, and what it measured. */
function defectSightForm(b: BridgeView, d: RegisterDefect, v: NonNullable<State['defectSight']>): HTMLElement {
  const after = (d.loggedAt ?? '').slice(0, 10);
  const visits = b.inspections.filter((i) => i.id && i.id !== d.inspectionId && i.date >= after);
  return h(
    'div',
    { class: 'dfd__form', 'data-sight': d.id },
    h(
      'label',
      { class: 'field', style: 'display:block;margin-top:8px' },
      h('span', { class: 'field__label', text: 'Inspection' }),
      h(
        'select',
        {
          id: 'dfs-insp',
          class: 'select',
          onchange: (e: Event) => actions.setDefectSightField('inspectionId', (e.target as HTMLSelectElement).value),
        },
        h('option', { value: '', text: visits.length ? 'Choose the visit…' : 'No later inspection on record' }),
        ...visits.map((i) =>
          h('option', { value: i.id!, selected: i.id === v.inspectionId, text: `${prettyDate(i.date)} · ${inspectionKindLabel(i)}` }),
        ),
      ),
    ),
    h(
      'label',
      { class: 'field', style: 'display:block;margin-top:8px' },
      h('span', { class: 'field__label', text: 'Severity then' }),
      h(
        'select',
        { id: 'dfs-sev', class: 'select', onchange: (e: Event) => actions.setDefectSightField('severity', (e.target as HTMLSelectElement).value) },
        ...DEFECT_SEVERITIES.map((sv) => h('option', { value: sv, selected: sv === v.severity, text: sv })),
      ),
    ),
    ...fieldsFor(d.code).map((f) =>
      h(
        'label',
        { class: 'field', style: 'display:block;margin-top:8px' },
        h('span', { class: 'field__label', text: `${f.label} (${f.unit})` }),
        h('input', {
          class: 'input',
          type: 'number',
          'data-key': f.key,
          value: v.values[f.key] ?? '',
          oninput: (e: Event) => actions.setDefectSightValue(f.key, (e.target as HTMLInputElement).value),
        }),
      ),
    ),
    h(
      'div',
      { style: 'display:flex;gap:6px;margin-top:8px' },
      button('Record', { sm: true, primary: true, onClick: () => void actions.submitDefectSight() }),
      button('Cancel', { sm: true, onClick: () => actions.cancelDefectSight() }),
    ),
  );
}

/**
 * Every visit that saw the defect (LC-62): the one that found it, then each
 * later one with the severity and measurement then, and how the main
 * measurement moved since the visit before.
 */
function defectVisits(b: BridgeView, d: RegisterDefect, s: Readonly<State>): HTMLElement {
  const history = s.defectHistory?.id === d.id ? s.defectHistory.history : null;
  const sightings = history?.sightings ?? (d.lastSeen ? [d.lastSeen] : []);
  const label = (id: string | null | undefined, fallback: string) => {
    const i = id ? b.inspections.find((x) => x.id === id) : undefined;
    return i ? inspectionKindLabel(i) : fallback;
  };
  const primary = fieldsFor(d.code).find((f) => f.primary)?.key ?? null;
  const rows: { on: string; visit: string; severity: string; q: RegisterDefect['quantity']; found: boolean }[] = [
    { on: d.loggedAt, visit: label(d.inspectionId, 'Found'), severity: d.severity, q: d.quantity, found: true },
    ...sightings.map((o) => ({ on: o.observedOn, visit: label(o.inspectionId, 'Inspection'), severity: o.severity, q: o.quantity, found: false })),
  ];
  let prev: number | null = null;
  return h(
    'div',
    { class: 'dfd__visits', 'data-visits': String(rows.length) },
    h('div', { class: 'dfd__cap', text: 'Visits' }),
    h(
      'ol',
      { class: 'dfd__tl' },
      ...rows.map((r) => {
        const v = primary && r.q ? (r.q as unknown as Record<string, unknown>)[primary] : undefined;
        const now = typeof v === 'number' ? v : null;
        const change = now !== null && prev !== null ? now - prev : null;
        if (now !== null) prev = now;
        return h(
          'li',
          { class: ['dfd__tl-item', r.found && 'dfd__tl-item--found'], 'data-visit': r.found ? 'found' : 'seen' },
          h('div', { class: 'dfd__tl-main', text: `${r.on ? prettyDate(r.on) : '—'} · ${r.found && r.visit !== 'Found' ? `${r.visit} · found` : r.visit}` }),
          h(
            'div',
            { class: 'dfd__tl-sub' },
            tag(r.severity, severityRank(r.severity)),
            h('span', { text: r.q ? describeQuantity(d.code, r.q) : 'Nothing measured' }),
            h('span', {
              class: 'dfd__change',
              'data-change': change === null ? '' : String(change),
              text: change === null ? '' : `${change > 0 ? '+' : ''}${trimNumber(change)} since last`,
            }),
          ),
        );
      }),
    ),
  );
}

/** Every status move, with who, when and why (LC-62). Only the live register keeps one. */
function defectStatusHistory(s: Readonly<State>, d: RegisterDefect): HTMLElement | null {
  const held = s.defectHistory?.id === d.id ? s.defectHistory : null;
  if (!held) return null;
  if (held.error) {
    return h('div', { class: 'dfd__statuses' }, h('div', { class: 'dfd__cap', text: 'Status history' }), h('p', { class: 'dfd__tl-sub', text: held.error }));
  }
  const steps = held.history?.statuses ?? [];
  if (!steps.length) return null;
  return h(
    'div',
    { class: 'dfd__statuses', 'data-steps': String(steps.length) },
    h('div', { class: 'dfd__cap', text: 'Status history' }),
    h(
      'ol',
      { class: 'dfd__tl' },
      ...steps.map((st) =>
        h(
          'li',
          { class: 'dfd__tl-item dfd__tl-item--ring', 'data-step': st.toStatus },
          h('div', { class: 'dfd__tl-main', text: st.fromStatus ? `${st.fromStatus} → ${st.toStatus}` : `Logged ${st.toStatus}` }),
          h('div', { class: 'dfd__tl-sub', text: [prettyDate(st.on), st.by ?? 'not attributable', st.repairRef, st.note].filter(Boolean).join(' · ') }),
        ),
      ),
    ),
  );
}

/** One line of the table: a group heading, or a defect. */
/**
 * One row of the defects table.
 *
 * `mayProgress` decides whether the status cell is a picker or a chip. Where a
 * defect has GOT TO is a decision about remedial work — see the note at the top
 * of functions/api/defects/[id].ts — so an inspector reads it and an engineer
 * moves it. Drawn as a chip rather than a disabled `select`, because a status
 * everybody can read is not a control that has been taken away; it is a fact
 * about the defect, and that is how it should look.
 */
function defectRow(
  row: DefectRow,
  mayProgress: boolean,
  refused: State['refused'] = null,
  /** Opens the Log defect dialog on a row's component; null where logging is refused. */
  log: ((row: DefectRow) => void) | null = null,
): HTMLElement {
  const d = row.defect;
  /* A sound component is entered from its row; one with defects already opens
     them, so another is added with the + beside its name. */
  const loggable = log !== null && row.value !== '' && row.isFirst;
  const logsOnClick = loggable && d === null;
  return h(
    'tr',
    {
      class: [
        !row.isComponent && 'tbl__group',
        d && !isOpenStatus(d.status) && 'is-muted',
        (d || logsOnClick) && 'is-clickable',
        loggable && 'tbl__loggable',
      ],
      title: d ? 'Show this inspection and its photographs' : logsOnClick ? `Log an inspection on ${row.label}` : undefined,
      onclick: d ? () => actions.showDefect(d.id) : logsOnClick ? () => log!(row) : undefined,
    },
    h(
      'td',
      { class: 'tbl__comp', style: { paddingLeft: `${14 + row.depth * 16}px` } },
      loggable
        ? h(
            'button',
            {
              type: 'button',
              class: 'tbl__log',
              title: `Log an inspection on ${row.label}`,
              'aria-label': `Log an inspection on ${row.label}`,
              onclick: (e: Event) => {
                e.stopPropagation();
                log!(row);
              },
            },
            icon(ICONS.plus, { size: 12 }),
          )
        : null,
      row.isFirst && row.hasChildren
        ? h(
            'span',
            {
              class: 'tbl__node',
              onclick: () => actions.toggleComponentNode(row.path),
            },
            icon(ICONS.chevronDown, {
              size: 10,
              width: 2,
              class: row.isOpen ? 'grp__chev' : 'grp__chev grp__chev--closed',
            }),
            h('span', { text: row.label }),
          )
        : h('span', { class: 'tbl__node', text: row.isFirst ? row.label : '' }),
    ),
    d
      ? h('td', { class: 'num display', style: 'letter-spacing:.05em', text: d.code })
      : h('td', {}),
    d ? h('td', { text: defectCodeLabel(d.code) }) : h('td', {}),
    d
      ? // Four steps of one hue rather than four colours — severity is an
        // ordering, and a reader should not have to remember whether orange
        // outranks red. See severityRank in data/derive.ts.
        h('td', {}, tag(d.severity, severityRank(d.severity)))
      : h('td', {}),
    d
      ? h(
          'td',
          {},
          mayProgress
            ? h(
                'select',
                {
                  class: 'select select--sm',
                  'aria-label': `Status of ${defectCodeLabel(d.code)} on ${row.label}`,
                  onclick: (e: Event) => e.stopPropagation(),
                  onchange: revertOnRefusal(d.status, (v) => actions.setDefectStatus(d.id, v)),
                },
                /* Where it stands and the moves that need nothing written (LC-62);
                   a repair, a verification and a reopen are made on the defect. */
                ...DEFECT_STATUSES.filter(
                  (st) => st === d.status || ((st === 'In Review' || st === 'Resolved') && defectMove(d.status, st) === 'forward'),
                ).map((st) => h('option', { value: st, selected: st === d.status, text: st })),
              )
            : h('span', {
                class: 'det-status',
                title: 'Moving an issue along is an engineer’s decision, not an inspection finding.',
                text: d.status,
              }),
          mayProgress ? refusalNote(refused, d.id) : null,
        )
      : h('td', {}),
    d
      ? h(
          'td',
          /* On the desk the three run as one line, the note and the measure cut
             to fit and the author kept whole (winui.css), so all three are on
             the cell's tooltip whole. */
          {
            class: 'tbl__run',
            title: [
              d.note,
              d.quantity ? describeQuantity(d.code, d.quantity) : null,
              d.author ?? 'not attributable',
            ]
              .filter(Boolean)
              .join('\n'),
          },
          h(
            'div',
            { class: 'tbl__run-in' },
            d.note ? h('div', { class: 'tbl__run-lead', text: d.note }) : null,
            /* How big it is, where anybody measured it — `QC-02`. In the note's
               cell rather than a column of its own for the same reason the
               inspection photographs are: most defects carry none, and an empty
               column on every row costs more than it tells anyone.

               The method is never omitted. A size with no provenance beside it
               reads as a fact, and a good proportion of these are somebody's
               estimate — which is exactly the distinction that has to survive as
               far as the cost estimate. */
            d.quantity
              ? h('div', { class: 'dfq' }, h('span', { text: describeQuantity(d.code, d.quantity) }))
              : null,
            /* Who, always. Null is "not attributable" and not "anonymous" — the
               same distinction every other authored row on this screen draws. */
            h('div', {
              style: 'color:var(--txt3);font-size:11px',
              text: d.author ?? 'not attributable',
            }),
          ),
        )
      : h('td', {}),
    d
      ? h('td', {
          class: 'num nowrap',
          style: 'color:var(--txt2)',
          text: d.loggedAt ? prettyDate(d.loggedAt) : '—',
        })
      : h('td', {}),
  );
}

/* --------------------------- Remedial work and cost ---------------------------

   `QC-05` and `QC-06`. The Bridge Inspection Summary has had a box for "the
   recommended repair work and ... an approximate cost" since the guideline was
   written; this is that box, on screen, built from the defects above.

   IT IS ONE TABLE, NOT TWO. An earlier sketch of this had a "recommendations"
   card and a "costs" card, and it was wrong: the whole point of the maintenance
   classification is that some recommendations cannot be costed, and separating
   them would put the most important work on the structure — the major repairs —
   on a card that nobody reading the money would open.

   So every recommendation is a row, and the ones with no figure carry the reason
   where the figure would be. A reader counting rows gets the work; a reader
   reading the last column sees immediately how much of it the register can put a
   number on.
   --------------------------------------------------------------------------- */

function remedialBlock(b: BridgeView, s: Readonly<State>): HTMLElement | null {
  /* Open defects only. A resolved defect's treatment is work somebody has
     already done, and pricing it would put completed work in a budget. */
  const defects = s.registerDefects.filter((d) => d.bridgeId === b.id && isOpenStatus(d.status));
  if (!defects.length) return null;

  const est = estimateFor(b, defects, s.rateTables, todayIso(), undefined, wordingMap(s.treatmentWording));
  if (!est.lines.length) return null;

  const priced = est.lines.filter((l) => l.amount !== null).length;

  const blockIsOpen = blockOpen(s, 'remedial');
  const head = foldHead(blockIsOpen, foldToggle(s, 'remedial'),
      'Recommended remedial work',
      est.table
        ? `${priced} of ${est.lines.length} priced · ${est.table.district} ${est.table.fiscalYear}`
        : `${est.lines.length} recommended · no approved rates loaded`,
      /* The district rates and the wording of this work are reference tables,
         at the foot of the explorer; the inspection summary sheet is on the
         Inspection ribbon. */
    );
  if (!blockIsOpen) return shutBlock(head);

  return h(
    'div',
    { class: 'det-block det-block--flush' },
    head,
    h(
      'table',
      { class: 'tbl' },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { text: 'Where' }),
          h('th', { text: 'Recommended work' }),
          h('th', { style: 'width:88px', text: 'Class' }),
          h('th', { style: 'width:110px', text: 'Quantity' }),
          h('th', { style: 'width:120px', text: 'Rate' }),
          h('th', { style: 'width:150px', text: 'Amount' }),
        ),
      ),
      h(
        'tbody',
        {},
        ...est.lines.map((l) =>
          h(
            'tr',
            {},
            h('td', { class: 'ellipsis', title: l.where, text: l.where }),
            /* No Edit here: the wording is reworded in its reference table, at the
               foot of the explorer. */
            h(
              'td',
              { class: 'tbl__prose' },
              h('div', { text: l.treatment.label }),
              h('div', { class: 'rem__note', text: l.treatment.note }),
            ),
            h(
              'td',
              {},
              h('span', {
                class: ['rem__class', !isCostable(l.klass) && 'rem__class--design'],
                title: CLASS_DIAGNOSTIC[l.klass],
                text: l.klass,
              }),
            ),
            h('td', {
              class: 'num nowrap',
              text: l.quantity === null ? '—' : `${trimNumber(l.quantity)} ${l.unit}`,
              /* The provenance of the quantity, on the quantity. It is the
                 number a reader would otherwise assume was measured. */
              title: l.method ? `${methodLabel(l.method)}` : 'No measurement recorded',
            }),
            h('td', {
              class: 'num nowrap',
              style: 'color:var(--txt2)',
              title: l.normRef ?? '',
              text: l.rate === null ? '—' : npr(l.rate),
            }),
            l.amount === null
              ? h('td', { class: 'rem__refusal tbl__prose', text: refusalText(l.refusal!) })
              : h('td', { class: 'num nowrap', text: npr(l.amount) }),
          ),
        ),
        ...est.additions.map((a) =>
          h(
            'tr',
            { class: 'rem__add' },
            h('td', { colspan: '5', text: `${a.label} at ${a.percent}%` }),
            h('td', { class: 'num nowrap', text: npr(a.amount) }),
          ),
        ),
        est.subtotal > 0
          ? h(
              'tr',
              { class: 'rem__total' },
              h('td', { colspan: '5', text: 'Approximate cost of the work priced above' }),
              h('td', { class: 'num nowrap', text: npr(est.total) }),
            )
          : null,
      ),
    ),
    /* The caveat travels with the total, always, and comes out of one function so
       that this card, the report and the printed sheet cannot each decide for
       themselves how much to admit. */
    h('div', { class: 'rem__caveat', text: totalCaveat(est) }),
  );
}

/**
 * Today, as an ISO date.
 *
 * A function rather than a constant so that a render an hour after midnight does
 * not go on reporting yesterday, and so the one place this application reads a
 * clock for a *displayed* value is greppable. The estimate itself takes the date
 * as an argument precisely so a check can pin it.
 */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/* --------------------------- Inspection history --------------------------- */

function conditionBlock(b: BridgeView, s: Readonly<State>): HTMLElement {
  const standing = inspectionStanding(b);
  /* Newest first. The register does not promise an order and an entry added
     today would otherwise land at the bottom of a list read from the top. */
  const history = b.inspections.slice().sort((x, y) => (x.date < y.date ? 1 : -1));

  const blockIsOpen = blockOpen(s, 'inspections');
  const head = foldHead(blockIsOpen, foldToggle(s, 'inspections'),
      'Overall inspection history',
      /* THE COUNT, not the caveat. "Ratings carried at source scale" is the
         sentence that stops a 4-of-5 being read beside a 6-of-9 as though the
         two were comparable, and it has moved onto the Rating and Scale columns
         it is about — see the table head below. A folded heading is a row in a
         contents page, and what it has to answer is whether there is anything
         in here worth opening. */
      history.length ? `${history.length} on file` : undefined,
      /* Both cycles, because this is the screen with room for both and the one
         where "when is this structure next due" is actually being asked. One
         chip could only ever show the worse of the two, which is how a
         principal inspection six years late stayed invisible behind a routine
         one done last month. */
      ...cycleChips(standing),
    );
  if (!blockIsOpen) return shutBlock(head);

  return h(
    'div',
    { class: 'det-block det-block--flush' },
    head,
    history.length
      ? h(
          'table',
          { class: 'tbl' },
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { text: 'Date' }),
              h('th', { text: 'Type' }),
              // Left-aligned now that the cell carries the stars as well: the
              // digits keep their own alignment inside the number's box.
              /* The caveat rides on the two columns it is about, now that the
                 heading carries the count instead. It is the sentence that
                 stops a 4-of-5 being read beside a 6-of-9 as though they were
                 the same measurement, so it goes where those numbers are
                 rather than at the top of a card. */
              h('th', { title: 'Ratings are carried at the scale their source used, never rescaled', text: 'Rating' }),
              h('th', { title: 'The scale that rating was recorded on', text: 'Scale' }),
              h('th', { text: 'State' }),
              h('th', { text: 'Note' }),
            ),
          ),
          h(
            'tbody',
            {},
            ...history.map((insp) =>
              h(
                'tr',
                {},
                h('td', { class: 'num nowrap', text: prettyDate(insp.date) }),
                h('td', { class: 'nowrap', text: inspectionKindLabel(insp) }),
                ratingCell(insp),
                /* The scale travels with the rating and is never converted: a 7
                   on a 0–10 sheet and a 3 on a 0–4 sheet are different claims,
                   and inventory sheets are not consistent about which they use. */
                h('td', { class: 'num nowrap', style: 'color:var(--txt2)', text: insp.scale }),
                inspectionStateCell(insp, can(s, 'inspection.accept'), s.refused),
                /* The note, and under it whatever was photographed at that
                   visit. In the note's cell rather than a column of its own:
                   most inspections carry none, and an empty sixth column on
                   every row would cost more than it tells anyone. */
                h(
                  'td',
                  { class: 'tbl__prose', style: 'color:var(--txt2)' },
                  h('span', { text: insp.note }),
                  elementBreakdown(insp),
                  inspectionThumbs(b.id, insp),
                ),
              ),
            ),
          ),
        )
      : h('div', { class: 'reg__empty', text: 'No inspections on file for this structure.' }),
  );
}

/**
 * The inspection history docked under the model, its heading lifted into the
 * panel's as the property panel lifts a lone block's. The condition trend is
 * its own page of the explorer.
 */
function inspectionsPanel(b: BridgeView, s: Readonly<State>): HTMLElement {
  const body = [conditionBlock(b, s)];
  const lifted = liftHead(body, 'Overall inspection history');
  return panel(
    {
      title: 'Overall inspection history',
      ...(lifted.meta !== undefined ? { meta: lifted.meta } : {}),
      actions: lifted.actions,
      class: ['det-inv', 'det-tabs', 'det-hist'],
    },
    ...body,
  );
}

/**
 * Condition per element, under the inspection that recorded it.
 *
 * Beside the overall rating rather than instead of it: the overall number is an
 * inspector's judgement of the structure and is not the mean of its parts, so
 * both are shown and neither is derived from the other.
 *
 * Elements that were not assessed are simply absent — there is no row saying
 * "not assessed", because twelve of those on a visit that rated one element
 * would bury the one thing the visit actually said.
 */
function elementBreakdown(insp: BridgeView['inspections'][number]): HTMLElement | null {
  const entries = insp.elements ?? [];
  if (!entries.length) return null;

  const max = scaleMax(insp.scale);

  return h(
    'span',
    {},
    h('div', {
      class: 'elbreak__cap',
      text: `Element condition — ${entries.length} assessed`,
    }),
    h(
      'span',
      { class: 'elbreak' },
      ...entries.map((e) =>
        h(
          'span',
          { class: 'elbreak__row' },
          h(
            'span',
            { class: 'elbreak__label ellipsis' },
            h('span', { text: elementLabel(e.element) }),
            /* An id from the vocabulary `QC-09` superseded, marked as such.
               Drawn beside the current eleven with no mark between them, an
               inspection rating "Pier foundation" would read as a twelfth item
               of the current form that nobody can find on it. The rating is
               still shown — it is a fact somebody recorded — and the mark is
               what makes the record legible as two vocabularies. */
            isRetiredElement(e.element)
              ? h('span', {
                  class: 'elbreak__old',
                  title:
                    'Recorded against the element list this register used before it adopted the ' +
                    'guideline’s inspection-form items. Kept as recorded rather than remapped.',
                  text: '†',
                })
              : null,
          ),
          h('span', {
            class: 'elbreak__v',
            // On its own scale, never rescaled — the same rule the overall
            // rating follows two columns to the left. `N` is not on the scale at
            // all: it is DoR's refusal to place the element on one.
            title: e.rating === 'N' ? CONDITION_N : undefined,
            text: e.rating === 'N' ? 'N' : max === null ? String(e.rating) : `${e.rating} / ${max}`,
          }),
          /* DoR's other two boxes. Absent rather than shown as a dash when the
             visit did not record them — this feature is new, and every
             inspection recorded before it has neither. A column of dashes down
             a history would read as eleven refusals rather than as a form that
             did not have the boxes. */
          e.extent === undefined
            ? null
            : h('span', {
                class: 'elbreak__band',
                title: `Extent ${e.extent} — ${EXTENT_DEFINITIONS[e.extent] ?? ''}`,
                text: `E${e.extent}`,
              }),
          e.urgency === undefined
            ? null
            : h('span', {
                class: ['elbreak__band', e.urgency >= 3 && 'elbreak__band--soon'],
                title: `Urgency ${e.urgency} — ${URGENCY_DEFINITIONS[e.urgency] ?? ''}`,
                text: `U${e.urgency}`,
              }),
        ),
      ),
    ),
  );
}

/**
 * The photographs taken at one inspection, as a strip of thumbnails.
 *
 * Each is a separate request to /api/inspection-photo and each is served
 * `immutable`, so a record opened twice fetches them once. Small on purpose:
 * enough to say a photograph exists and to click, not enough to read a crack —
 * that is what the enlarged view is for, exactly as with the defect tiles above.
 */
function inspectionThumbs(bridgeId: string, insp: BridgeView['inspections'][number]): HTMLElement | null {
  const photos = insp.photos ?? [];
  if (!photos.length) return null;

  return h(
    'span',
    { class: 'insp-thumbs' },
    ...photos.map((photo, i) =>
      h(
        'button',
        {
          type: 'button',
          class: 'insp-thumbs__btn',
          title: `Photograph ${i + 1} of ${photos.length}${photo.author ? ` · ${photo.author}` : ''}`,
          'aria-label': `Enlarge photograph ${i + 1} from the inspection of ${prettyDate(insp.date)}`,
          onclick: () => actions.openInspectionPhoto(photo.id),
        },
        h('img', {
          src: api.inspectionPhotoUrl(bridgeId, photo.id),
          alt: `Photograph ${i + 1} from the inspection of ${prettyDate(insp.date)}`,
          loading: 'lazy',
        }),
      ),
    ),
  );
}

/**
 * The rating, and the same rating drawn on its own scale.
 *
 * One star per step of whatever scale the sheet used — four for a 0-4 record, ten
 * for a 0-10 one — so the mark is legible without doing arithmetic against the
 * column beside it. A 0-100 sheet gets a proportion bar instead: a hundred stars
 * cannot be drawn, and rescaling it to ten would be a conversion, which is the
 * one thing this table does not do to a rating.
 *
 * The number stays in a fixed box so the digits still line up down the column
 * while the stars all start from the same edge.
 */
function ratingCell(insp: BridgeView['inspections'][number]): HTMLElement {
  const max = scaleMax(insp.scale);

  return h(
    'td',
    { class: 'rating' },
    h('span', { class: 'num rating__n', text: String(insp.rating) }),
    max === null
      ? null
      : max <= STAR_SCALE_LIMIT
        ? stars(insp.rating, max)
        : h(
            'span',
            {
              class: 'rating__bar',
              title: `${insp.rating} out of ${max}`,
              'aria-label': `${insp.rating} out of ${max}`,
              role: 'img',
            },
            meter(`${Math.max(0, Math.min(1, insp.rating / max)) * 100}%`, 'var(--accent-solid)', 5),
          ),
  );
}

/* --------------------------- photos --------------------------- */

/**
 * Photographs of the structure.
 *
 * The real ones come from its defects: an inspector attaching a photo to one in
 * the Defects inspector is the only path in this system that puts a photograph of
 * a bridge somewhere it survives, so that is what this card shows. Each is
 * captioned with the component it was logged against and its severity, because a
 * photograph of a crack means little without knowing which pier it is on, and
 * clicking one opens it full size — see screens/photoDialog.ts. A tile is 130px
 * wide, which is enough to say a defect was photographed and not enough to read
 * the photograph.
 *
 * Filtered to the selected structure, because `registerDefects` holds the whole
 * network — one read for the overview's counts, and every screen takes its slice.
 *
 * The drop slots stand in only while there are none. Nothing dropped there
 * persists past the session — there is no upload endpoint — so they are a way to
 * hold an image against the record while reading it, not storage.
 */
function photosBlock(b: BridgeView, s: Readonly<State>): HTMLElement {
  const photographed = s.registerDefects.filter((d) => d.bridgeId === b.id && d.hasPhoto);
  const total = photographed.reduce((sum, d) => sum + defectPhotoCount(d), 0);

  const blockIsOpen = blockOpen(s, 'photos');
  const head = foldHead(
    blockIsOpen,
    foldToggle(s, 'photos'),
    'Photos',
    total ? String(total) : undefined,
  );
  if (!blockIsOpen) return shutBlock(head);

  return h(
    'div',
    { class: 'det-block' },
    head,
    photographed.length
      ? h('div', { class: 'photogrid' }, ...photographed.flatMap((d) => defectPhotos(d)))
      : h(
          'div',
          { class: 'photogrid' },
          ...Array.from({ length: 3 }, (_, i) =>
            h('div', {}, imageSlot(`det-${b.id}-p${i + 1}`, 'Photo', { radius: 2, compact: true })),
          ),
        ),
  );
}

/** Every photograph of one defect, one tile each. */
function defectPhotos(d: RegisterDefect): HTMLElement[] {
  const count = defectPhotoCount(d);
  return Array.from({ length: count }, (_, i) => defectPhoto(d, i + 1, count));
}

/** One defect photograph, captioned with what it is of. Opens full size. */
function defectPhoto(d: RegisterDefect, n: number, count: number): HTMLElement {
  const where =
    `${d.component ?? unplacedLabel(d)} · ${defectCodeLabel(d.code)}` +
    (count > 1 ? ` · ${n}/${count}` : '');
  return h(
    'button',
    {
      class: 'photo',
      type: 'button',
      title: `Enlarge — ${where}`,
      /* By the modeller's handle, not the row id: that is what the photo
         endpoint is addressed by, and what the dialog looks the defect up on. */
      onclick: () => actions.openPhoto(d.modelDefectId, n),
    },
    h('img', {
      class: 'photo__img',
      src: api.defectPhotoSrc(d, n, true),
      alt: `${where} — ${d.severity}${d.note ? `. ${d.note}` : ''}`,
      loading: 'lazy',
      /* A photo whose bytes will not decode leaves the caption and an empty frame
         rather than a broken-image glyph. The defect is still a real record. */
      onerror: (e: Event) => (e.currentTarget as HTMLElement).classList.add('photo__img--gone'),
    }),
    h(
      'div',
      { class: 'photo__cap' },
      h('span', { class: 'photo__where', title: `${where}\n${d.note}`, text: where }),
      tag(d.severity, severityRank(d.severity)),
    ),
  );
}
